import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './App.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function App() {
  const [currentView, setCurrentView] = useState('public');
  const [currentArticleId, setCurrentArticleId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('tfun_admin_token'));

  // Check URL on mount for direct links
  useEffect(() => {
    const path = window.location.pathname;
    const hash = window.location.hash;
    const savedToken = localStorage.getItem('tfun_admin_token');
    
    // Parse hash for video links
    if (hash.startsWith('#video-')) {
      const videoId = hash.substring(7); // Remove '#video-' prefix
      setCurrentView(`video-${videoId}`);
      return;
    }
    
    // Parse URL for direct article links
    if (path.startsWith('/article/')) {
      const articleId = path.split('/article/')[1];
      setCurrentArticleId(articleId);
      setCurrentView('article');
      return;
    }
    
    if (path.startsWith('/admin/edit/')) {
      const articleId = path.split('/admin/edit/')[1];
      setCurrentArticleId(articleId);
      // Check if admin is authenticated
      if (savedToken) {
        verifyTokenAndSetView('admin-edit', savedToken);
      } else {
        setCurrentView('admin-login');
      }
      return;
    }
    
    if (path === '/admin' || path.startsWith('/admin')) {
      if (savedToken) {
        verifyTokenAndSetView('admin-dashboard', savedToken);
      } else {
        setCurrentView('admin-login');
      }
      return;
    }
  }, []);

  const verifyTokenAndSetView = async (targetView, authToken) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/verify`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIsAuthenticated(true);
        setCurrentAdmin(data.admin);
        setToken(authToken);
        setCurrentView(targetView);
      } else {
        localStorage.removeItem('tfun_admin_token');
        setCurrentView('admin-login');
      }
    } catch (error) {
      localStorage.removeItem('tfun_admin_token');
      setCurrentView('admin-login');
    }
  };

  // Handle URL parameters for direct access
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const viewParam = urlParams.get('view');
    const pathName = window.location.pathname;
    
    // Handle /call-queue URL
    if (pathName === '/call-queue') {
      const adminToken = localStorage.getItem('tfun_admin_token');
      if (adminToken) {
        setCurrentView('call-queue');
        setToken(adminToken);
      } else {
        // Redirect to login if not authenticated
        window.location.href = '/';
      }
    }
    // Handle other view parameters
    else if (viewParam && ['admin', 'ai-analysis', 'train-ai', 'qa-questions'].includes(viewParam)) {
      const adminToken = localStorage.getItem('tfun_admin_token');
      if (adminToken) {
        setCurrentView(viewParam);
        setToken(adminToken);
      }
    }
  }, []);

  const handleViewChange = (view, articleId = null) => {
    setCurrentView(view);
    setCurrentArticleId(articleId);
    
    // Update URL for call-queue to allow direct access
    if (view === 'call-queue') {
      window.history.pushState({}, '', '/call-queue');
    } else if (view === 'public') {
      window.history.pushState({}, '', '/');
    } else if (view === 'article' && articleId) {
      window.history.pushState({}, '', `/article/${articleId}`);
    } else if (view === 'admin-edit' && articleId) {
      window.history.pushState({}, '', `/admin/edit/${articleId}`);
    } else if (view === 'admin-dashboard') {
      window.history.pushState({}, '', '/admin');
    } else if (view === 'public-videos') {
      window.history.pushState({}, '', '/videos');
    } else if (view.startsWith('video-')) {
      const videoId = view.split('video-')[1];
      window.history.pushState({}, '', `/#video-${videoId}`);
    } else {
      window.history.pushState({}, '', `/?view=${view}`);
    }
  };

  const handleLogin = (adminData, authToken) => {
    setIsAuthenticated(true);
    setCurrentAdmin(adminData);
    setToken(authToken);
    localStorage.setItem('tfun_admin_token', authToken);
    setCurrentView('admin-dashboard');
    window.history.pushState({}, '', '/admin');
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentAdmin(null);
    setToken(null);
    localStorage.removeItem('tfun_admin_token');
    setCurrentView('public');
    window.history.pushState({}, '', '/');
  };

  if (currentView === 'public') {
    return <PublicView onViewChange={handleViewChange} />;
  }

  if (currentView === 'article') {
    return <ArticleView articleId={currentArticleId} onViewChange={handleViewChange} />;
  }

  if (currentView === 'admin-login') {
    return <AdminLogin onLogin={handleLogin} onViewChange={handleViewChange} />;
  }

  if (currentView === 'admin-dashboard') {
    return <AdminDashboard token={token} onLogout={handleLogout} onViewChange={handleViewChange} />;
  }

  if (currentView === 'admin-edit') {
    return <AdminEditArticle articleId={currentArticleId} token={token} onLogout={handleLogout} onViewChange={handleViewChange} />;
  }

  if (currentView === 'admin-categories') {
    return <AdminCategories token={token} onLogout={handleLogout} onViewChange={handleViewChange} />;
  }

  if (currentView === 'admin-users') {
    return <AdminUsers token={token} onLogout={handleLogout} onViewChange={handleViewChange} />;
  }

  if (currentView === 'train-ai') {
    return <TrainAI token={token} onLogout={handleLogout} onViewChange={handleViewChange} />;
  }

  if (currentView === 'qa-questions') {
    return <QAQuestions token={token} onLogout={handleLogout} onViewChange={handleViewChange} />;
  }

  if (currentView === 'countdown') {
    return <CountdownPage token={token} onLogout={handleLogout} onViewChange={handleViewChange} />;
  }

  if (currentView === 'call-queue') {
    return <CallQueue />;
  }

  if (currentView === 'public-videos') {
    return <PublicVideos onViewChange={handleViewChange} />;
  }

  if (currentView.startsWith('video-')) {
    const videoId = currentView.split('video-')[1];
    return <VideoDetail videoId={videoId} onViewChange={handleViewChange} />;
  }

  return <div>Loading...</div>;
}

function PublicView({ onViewChange }) {
  const [articles, setArticles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [articlesPerPage, setArticlesPerPage] = useState(10);

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/public/articles`);
      if (response.ok) {
        const data = await response.json();
        setArticles(data);
      }
    } catch (error) {
      console.error('Failed to load articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = searchQuery.trim() 
    ? articles.filter(article => 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (article.tags && article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())))
      )
    : [];

  // Pagination logic
  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);
  const startIndex = (currentPage - 1) * articlesPerPage;
  const endIndex = startIndex + articlesPerPage;
  const currentArticles = filteredArticles.slice(startIndex, endIndex);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleArticlesPerPageChange = (newPerPage) => {
    setArticlesPerPage(newPerPage);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-3xl font-bold text-white mb-4">The Follow Up Ninja Help Center</h1>
        <p className="text-gray-300 mb-8">Find instant answers to your questions about TFUN, Member Magnet, Student Magnet, and More!</p>

        <div className="mb-6">
          <input
            type="text"
            placeholder="Start typing your question for an instant answer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-800 text-white placeholder-gray-400"
          />
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            <p className="mt-4 text-gray-300">Loading articles...</p>
          </div>
        ) : (
          <>
            {/* Show articles only if there's a search query */}
            {searchQuery.trim() && (
              <>
                {/* Articles per page selector */}
                {filteredArticles.length > 0 && (
                  <div className="mb-4 flex justify-between items-center">
                    <p className="text-sm text-gray-400">
                      Showing {startIndex + 1} to {Math.min(endIndex, filteredArticles.length)} of {filteredArticles.length} results
                    </p>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-400">Articles per page:</span>
                      <select
                        value={articlesPerPage}
                        onChange={(e) => handleArticlesPerPageChange(parseInt(e.target.value))}
                        className="px-3 py-1 border border-gray-600 rounded text-sm focus:ring-2 focus:ring-red-500 bg-gray-800 text-white"
                      >
                        <option value={10}>10</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Articles */}
                <div className="space-y-4">
                  {currentArticles.map(article => (
                    <div
                      key={article.id}
                      onClick={() => onViewChange('article', article.id)}
                      className="bg-gray-800 p-6 rounded-lg shadow hover:shadow-md cursor-pointer border border-gray-700 hover:bg-gray-700"
                    >
                      <h3 className="text-xl font-semibold text-white mb-2">{article.title}</h3>
                      <p className="text-gray-300">
                        {article.content.replace(/<[^>]*>/g, '').substring(0, 150)}...
                      </p>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8 flex justify-center">
                    <nav className="flex items-center space-x-1">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-2 text-sm text-gray-400 hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-2 text-sm ${
                            currentPage === page
                              ? 'bg-red-600 text-white'
                              : 'text-gray-400 hover:text-gray-200'
                          } rounded`}
                        >
                          {page}
                        </button>
                      ))}
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 text-sm text-gray-400 hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                )}

                {/* No results message */}
                {filteredArticles.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-gray-400">No articles found matching your search.</p>
                  </div>
                )}

                {/* Support ticket message and button */}
                <div className="mt-8 text-center py-6 border-t border-gray-700">
                  <p className="text-gray-300 mb-4">Can't find the answer you're looking for?</p>
                  <a
                    href="https://studentmagnet.com/support-ticket-7175"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-6 py-3 text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: '#d4161d' }}
                  >
                    Submit a Support Ticket
                  </a>
                </div>
              </>
            )}

            {/* Show helpful message when no search query */}
            {!searchQuery.trim() && (
              <div className="text-center py-12 space-y-6">
                <p className="text-gray-300 text-lg">Start typing in the search box above to find answers to your questions!</p>
                
                <div className="pt-4 space-y-4">
                  <button
                    onClick={() => onViewChange('public-videos')}
                    className="inline-block px-6 py-3 text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: '#d4161d' }}
                  >
                    Browse Recent Support Call Archives
                  </button>
                  <div>
                    <a
                      href="https://studentmagnet.com/submit-a-question"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block px-6 py-3 text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: '#d4161d' }}
                    >
                      Register for the Next Support Call
                    </a>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Admin login moved to bottom */}
        <div className="fixed bottom-4 right-4">
          <button
            onClick={() => onViewChange('admin-login')}
            className="text-red-400 hover:text-red-200 text-sm bg-gray-800 px-3 py-2 rounded border border-gray-600"
          >
            Admin Login
          </button>
        </div>
      </div>
    </div>
  );
}

function ArticleView({ articleId, onViewChange }) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticle();
  }, [articleId]);

  const loadArticle = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/public/articles/${articleId}`);
      if (response.ok) {
        setArticle(await response.json());
      }
    } catch (error) {
      console.error('Failed to load article:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <p className="mt-4 text-gray-300">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-black">
        <div className="max-w-4xl mx-auto p-6">
          <button
            onClick={() => onViewChange('public')}
            className="text-red-400 hover:text-red-200 mb-4"
          >
            ← Back to The Follow Up Ninja Help Center
          </button>
          <h1 className="text-2xl font-bold text-white">Article not found</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-4xl mx-auto p-6">
        <button
          onClick={() => onViewChange('public')}
          className="text-red-400 hover:text-red-200 mb-6"
        >
          ← Back to The Follow Up Ninja Help Center
        </button>
        
        <article className="bg-gray-800 p-8 rounded-lg shadow border border-gray-700">
          <h1 className="text-3xl font-bold text-white mb-4">{article.title}</h1>
          
          <div 
            className="prose max-w-none text-white article-content mb-6"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {article.media_urls && article.media_urls.length > 0 && (
            <div className="mt-8">
              <h3 className="text-xl font-semibold text-white mb-4">Media</h3>
              {article.media_urls.map((url, index) => (
                <div key={index} className="mb-4">
                  {url.includes('youtube.com') || url.includes('vimeo.com') ? (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="text-red-400 hover:underline">
                      {url}
                    </a>
                  ) : (
                    <video controls className="w-full max-w-2xl">
                      <source src={url.startsWith('http') ? url : `${API_BASE}${url}`} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  )}
                </div>
              ))}
            </div>
          )}
        </article>
      </div>
    </div>
  );
}

function AdminLogin({ onLogin, onViewChange }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        onLogin(data.admin, data.access_token);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Login failed');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="max-w-md w-full">
        <div className="bg-gray-800 p-8 rounded-lg shadow border border-gray-700">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">Admin Login</h2>
          
          {error && (
            <div className="bg-red-900 border border-red-600 text-red-200 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-gray-300 text-sm font-bold mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white placeholder-gray-400"
                required
              />
            </div>

            <div className="mb-6">
              <label className="block text-gray-300 text-sm font-bold mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white placeholder-gray-400"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              onClick={() => onViewChange('public')}
              className="text-red-400 hover:text-red-200 text-sm"
            >
              Back to The Follow Up Ninja Help Center
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminDashboard({ token, onLogout, onViewChange }) {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ai-analysis'); // Changed default tab

  useEffect(() => {
    loadArticles();
    loadCategories();
  }, []);

  const loadArticles = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/articles`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        setArticles(await response.json());
      }
    } catch (error) {
      console.error('Failed to load articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/categories`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        setCategories(await response.json());
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleEditArticle = (article) => {
    onViewChange('admin-edit', article.id);
  };

  const handleNewArticle = () => {
    onViewChange('admin-edit', 'new');
  };

  const handleCopyPublicLink = (articleId) => {
    const publicUrl = `${window.location.origin}/article/${articleId}`;
    navigator.clipboard.writeText(publicUrl).then(() => {
      alert('Public link copied to clipboard!');
    });
  };

  const handleCopyAdminLink = (articleId) => {
    const adminUrl = `${window.location.origin}/admin/edit/${articleId}`;
    navigator.clipboard.writeText(adminUrl).then(() => {
      alert('Admin edit link copied to clipboard!');
    });
  };

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
          <div className="space-x-4">
            <button
              onClick={() => onViewChange('public')}
              className="text-red-400 hover:text-red-200"
            >
              View Public Site
            </button>
            <button
              onClick={onLogout}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-700">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('ai-analysis')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'ai-analysis'
                    ? 'border-red-500 text-red-400'
                    : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-300'
                }`}
              >
                🤖 AI Analysis
              </button>
              <button
                onClick={() => setActiveTab('articles')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'articles'
                    ? 'border-red-500 text-red-400'
                    : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-300'
                }`}
              >
                Articles ({articles.length})
              </button>
              <button
                onClick={() => setActiveTab('whitelabel')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'whitelabel'
                    ? 'border-red-500 text-red-400'
                    : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-300'
                }`}
              >
                🏷️ Whitelabel Articles
              </button>
              <button
                onClick={() => setActiveTab('videos')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'videos'
                    ? 'border-red-500 text-red-400'
                    : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-300'
                }`}
              >
                Support Videos
              </button>
              <button
                onClick={() => onViewChange('qa-questions')}
                className="py-2 px-1 border-b-2 border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-300 font-medium text-sm"
              >
                Q&A Questions
              </button>
              <button
                onClick={() => onViewChange('countdown')}
                className="py-2 px-1 border-b-2 border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-300 font-medium text-sm"
              >
                Countdown
              </button>
              <button
                onClick={() => onViewChange('admin-categories')}
                className="py-2 px-1 border-b-2 border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-300 font-medium text-sm"
              >
                Categories ({categories.length})
              </button>
              <button
                onClick={() => onViewChange('admin-users')}
                className="py-2 px-1 border-b-2 border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-300 font-medium text-sm"
              >
                Admin Users
              </button>
              <button
                onClick={() => onViewChange('train-ai')}
                className="py-2 px-1 border-b-2 border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-300 font-medium text-sm"
              >
                Train AI
              </button>
            </nav>
          </div>
        </div>

        {/* AI Analysis Tab Content */}
        {activeTab === 'ai-analysis' && (
          <AIAnalysisTab
            token={token}
            onCreateArticle={handleNewArticle}
            onViewChange={onViewChange}
          />
        )}

        {/* Articles Tab Content */}
        {activeTab === 'articles' && (
          <ArticlesTab
            articles={articles}
            loading={loading}
            onEditArticle={handleEditArticle}
            onNewArticle={handleNewArticle}
            onCopyPublicLink={handleCopyPublicLink}
            onCopyAdminLink={handleCopyAdminLink}
            token={token}
            onReload={loadArticles}
          />
        )}

        {/* Videos Tab Content */}
        {activeTab === 'videos' && (
          <VideosTab
            token={token}
            onViewChange={onViewChange}
          />
        )}

        {/* Whitelabel Articles Tab Content */}
        {activeTab === 'whitelabel' && (
          <WhitelabelTab
            token={token}
            categories={categories}
            onViewChange={onViewChange}
            onReload={loadArticles}
          />
        )}
      </div>
    </div>
  );
}

// AI Analysis Tab Component
function AIAnalysisTab({ token, onCreateArticle, onViewChange }) {
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [recentTickets, setRecentTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [message, setMessage] = useState('');
  const [showIgnoreModal, setShowIgnoreModal] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketAnalysisDetails, setTicketAnalysisDetails] = useState(null);
  const [loadingTicketDetails, setLoadingTicketDetails] = useState(false);

  useEffect(() => {
    loadAnalysisData();
  }, []);

  const loadAnalysisData = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/ai-analysis`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnalysisData(data.suggestions_by_priority);
        setRecentTickets(data.recent_tickets);
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to load AI analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateArticle = async (suggestion) => {
    try {
      const response = await fetch(`${API_BASE}/api/ai-analysis/create-article`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(suggestion)
      });

      if (response.ok) {
        const result = await response.json();
        showMessage('Article created successfully! Opening in editor...', 'success');
        
        // Open article in editor
        setTimeout(() => {
          onViewChange('admin-edit', result.article.id);
        }, 1000);
        
        // Reload analysis data
        loadAnalysisData();
      } else {
        showMessage('Failed to create article', 'error');
      }
    } catch (error) {
      console.error('Error creating article:', error);
      showMessage('Error creating article', 'error');
    }
  };

  const handleExistingArticleFeedback = async (suggestion, feedbackType) => {
    try {
      const response = await fetch(`${API_BASE}/api/ai-analysis/existing-article-feedback`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          suggestion_id: suggestion.analysis_id,
          feedback_type: feedbackType,
          suggestion_data: suggestion
        })
      });

      if (response.ok) {
        showMessage(`Feedback recorded: ${feedbackType === 'good' ? 'Article marked as helpful' : 'Article marked as not relevant'}`, 'success');
        loadAnalysisData(); // Reload to remove the suggestion
      } else {
        showMessage('Failed to record feedback', 'error');
      }
    } catch (error) {
      console.error('Error recording feedback:', error);
      showMessage('Error recording feedback', 'error');
    }
  };

  const handleIgnoreSuggestion = async (suggestion) => {
    if (!suggestion) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/ai-analysis/suggestion/${suggestion.analysis_id || suggestion.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showMessage('Suggestion ignored and feedback recorded for AI training', 'success');
        setShowIgnoreModal(null);
        loadAnalysisData(); // Reload data
      } else {
        showMessage('Failed to ignore suggestion', 'error');
      }
    } catch (error) {
      console.error('Error ignoring suggestion:', error);
      showMessage('Error ignoring suggestion', 'error');
    }
  };

  const handleTicketClick = async (ticket) => {
    setSelectedTicket(ticket);
    setLoadingTicketDetails(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/ai-analysis/ticket/${ticket.id}/details`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const details = await response.json();
        setTicketAnalysisDetails(details);
      } else {
        showMessage('Failed to load ticket analysis details', 'error');
      }
    } catch (error) {
      console.error('Error loading ticket details:', error);
      showMessage('Error loading ticket details', 'error');
    } finally {
      setLoadingTicketDetails(false);
    }
  };

  const closeTicketModal = () => {
    setSelectedTicket(null);
    setTicketAnalysisDetails(null);
    setLoadingTicketDetails(false);
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(''), 3000);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-500 bg-red-900/20';
      case 'high': return 'text-orange-500 bg-orange-900/20';
      case 'medium': return 'text-yellow-500 bg-yellow-900/20';
      case 'low': return 'text-green-500 bg-green-900/20';
      default: return 'text-gray-500 bg-gray-900/20';
    }
  };

  const getAllSuggestions = () => {
    if (!analysisData) return [];
    
    const allSuggestions = [];
    Object.entries(analysisData).forEach(([priority, suggestions]) => {
      suggestions.forEach(suggestion => {
        allSuggestions.push({
          ...suggestion,
          priority
        });
      });
    });
    
    return allSuggestions;
  };

  const getFilteredSuggestions = () => {
    const allSuggestions = getAllSuggestions();
    
    if (selectedPriority === 'all') {
      return allSuggestions;
    }
    
    return allSuggestions.filter(s => s.priority === selectedPriority);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <p className="mt-4 text-gray-300">Loading AI analysis...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Toast Message */}
      {message && (
        <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
          message.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
        }`}>
          {message.text}
        </div>
      )}

      {/* Ticket Analysis Details Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden border border-gray-700">
            <div className="flex justify-between items-center p-6 border-b border-gray-700">
              <h3 className="text-xl font-semibold text-white">AI Analysis Details</h3>
              <button
                onClick={closeTicketModal}
                className="text-gray-400 hover:text-gray-200 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {loadingTicketDetails ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                    <p className="mt-4 text-gray-300">Loading analysis details...</p>
                  </div>
                </div>
              ) : ticketAnalysisDetails ? (
                <div className="space-y-6">
                  {/* Ticket Information */}
                  <div className="bg-gray-900 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-white mb-3">📋 Support Ticket</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <span className="text-sm text-gray-400">Customer:</span>
                        <p className="text-white">{ticketAnalysisDetails.ticket.first_name} {ticketAnalysisDetails.ticket.last_name}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-400">Business:</span>
                        <p className="text-white">{ticketAnalysisDetails.ticket.business_name || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-400">Category:</span>
                        <p className="text-white">{ticketAnalysisDetails.ticket.category}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-400">Urgency:</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          ticketAnalysisDetails.ticket.urgency === 'high' ? 'bg-red-900/20 text-red-400' :
                          ticketAnalysisDetails.ticket.urgency === 'medium' ? 'bg-yellow-900/20 text-yellow-400' :
                          'bg-green-900/20 text-green-400'
                        }`}>
                          {ticketAnalysisDetails.ticket.urgency?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <span className="text-sm text-gray-400">Subject:</span>
                        <p className="text-white">{ticketAnalysisDetails.ticket.subject}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-400">Message:</span>
                        <p className="text-gray-300">{ticketAnalysisDetails.ticket.message}</p>
                      </div>
                      {ticketAnalysisDetails.ticket.expectations_vs_actual && (
                        <div>
                          <span className="text-sm text-gray-400">Expectations vs Actual:</span>
                          <p className="text-gray-300">{ticketAnalysisDetails.ticket.expectations_vs_actual}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* AI Analysis Process */}
                  <div className="bg-blue-900/20 rounded-lg p-4 border border-blue-700">
                    <h4 className="text-lg font-semibold text-blue-300 mb-3">🤖 AI Analysis Process</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <span className="text-sm text-blue-400">Model Used:</span>
                        <p className="text-white">{ticketAnalysisDetails.analysis_metadata?.ai_model_used || 'GPT-4o'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-blue-400">Processing Time:</span>
                        <p className="text-white">{ticketAnalysisDetails.analysis_metadata?.analysis_duration || '~30 seconds'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-blue-400">Confidence Score:</span>
                        <p className="text-white">{ticketAnalysisDetails.analysis?.confidence_score || 85}%</p>
                      </div>
                      <div>
                        <span className="text-sm text-blue-400">Priority Level:</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          ticketAnalysisDetails.analysis?.priority_level === 'urgent' ? 'bg-red-900/20 text-red-400' :
                          ticketAnalysisDetails.analysis?.priority_level === 'high' ? 'bg-orange-900/20 text-orange-400' :
                          ticketAnalysisDetails.analysis?.priority_level === 'medium' ? 'bg-yellow-900/20 text-yellow-400' :
                          'bg-green-900/20 text-green-400'
                        }`}>
                          {ticketAnalysisDetails.analysis?.priority_level?.toUpperCase()}
                        </span>
                      </div>
                    </div>

                    {/* Processing Steps */}
                    <div>
                      <span className="text-sm text-blue-400">Analysis Steps:</span>
                      <ol className="list-decimal list-inside mt-2 space-y-1">
                        {(ticketAnalysisDetails.analysis?.reasoning_process || ticketAnalysisDetails.analysis_metadata?.processing_steps || []).map((step, index) => (
                          <li key={index} className="text-gray-300 text-sm">{step.replace(/^\d+\.\s*/, '')}</li>
                        ))}
                      </ol>
                    </div>

                    {/* Methodology */}
                    {ticketAnalysisDetails.analysis?.analysis_methodology && (
                      <div className="mt-4">
                        <span className="text-sm text-blue-400">Methodology:</span>
                        <p className="text-gray-300 text-sm mt-1">{ticketAnalysisDetails.analysis.analysis_methodology}</p>
                      </div>
                    )}
                  </div>

                  {/* Sources Used */}
                  {ticketAnalysisDetails.analysis?.sources_used && ticketAnalysisDetails.analysis.sources_used.length > 0 && (
                    <div className="bg-green-900/20 rounded-lg p-4 border border-green-700">
                      <h4 className="text-lg font-semibold text-green-300 mb-3">📚 Sources Referenced</h4>
                      <div className="space-y-3">
                        {ticketAnalysisDetails.analysis.sources_used.map((source, index) => (
                          <div key={index} className="bg-gray-900 rounded-lg p-3">
                            <div className="flex items-center gap-3 mb-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                source.type === 'existing_article' ? 'bg-blue-900/20 text-blue-400' :
                                source.type === 'training_data' ? 'bg-purple-900/20 text-purple-400' :
                                'bg-gray-900/20 text-gray-400'
                              }`}>
                                {source.type?.replace('_', ' ').toUpperCase()}
                              </span>
                              {source.match_confidence && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-900/20 text-yellow-300">
                                  {source.match_confidence.toUpperCase()} MATCH
                                </span>
                              )}
                              {source.relevance_score && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-900/20 text-blue-300">
                                  {source.relevance_score}/10 RELEVANCE
                                </span>
                              )}
                            </div>
                            <h5 className="text-white font-medium">{source.title || source.source}</h5>
                            {source.content_preview && (
                              <p className="text-gray-400 text-sm mt-1">{source.content_preview}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* AI Analysis Summary */}
                  <div className="bg-gray-900 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-white mb-3">💡 Analysis Summary</h4>
                    <p className="text-gray-300 mb-4">{ticketAnalysisDetails.analysis?.analysis_summary}</p>
                    
                    {ticketAnalysisDetails.analysis?.patterns_identified && (
                      <div className="mb-4">
                        <span className="text-sm text-gray-400">Patterns Identified:</span>
                        <ul className="list-disc list-inside mt-1 space-y-1">
                          {ticketAnalysisDetails.analysis.patterns_identified.map((pattern, index) => (
                            <li key={index} className="text-gray-300 text-sm">{pattern}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {ticketAnalysisDetails.analysis?.recommended_actions && (
                      <div>
                        <span className="text-sm text-gray-400">Recommended Actions:</span>
                        <ul className="list-disc list-inside mt-1 space-y-1">
                          {ticketAnalysisDetails.analysis.recommended_actions.map((action, index) => (
                            <li key={index} className="text-gray-300 text-sm">{action}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Suggestions Generated */}
                  {ticketAnalysisDetails.analysis?.suggestions && ticketAnalysisDetails.analysis.suggestions.length > 0 && (
                    <div className="bg-purple-900/20 rounded-lg p-4 border border-purple-700">
                      <h4 className="text-lg font-semibold text-purple-300 mb-3">🎯 AI Suggestions Generated</h4>
                      <div className="space-y-3">
                        {ticketAnalysisDetails.analysis.suggestions.map((suggestion, index) => (
                          <div key={index} className="bg-gray-900 rounded-lg p-3">
                            <div className="flex items-center gap-3 mb-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                suggestion.suggestion_type === 'existing_article' 
                                  ? 'bg-green-900/20 text-green-400' 
                                  : 'bg-blue-900/20 text-blue-400'
                              }`}>
                                {suggestion.suggestion_type?.replace('_', ' ').toUpperCase()}
                              </span>
                            </div>
                            <h5 className="text-white font-medium mb-2">{suggestion.title}</h5>
                            <p className="text-gray-400 text-sm">{suggestion.reasoning}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">❌</div>
                  <h3 className="text-xl font-semibold text-white mb-2">Analysis Details Not Available</h3>
                  <p className="text-gray-400">Unable to load detailed analysis for this ticket.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ignore Confirmation Modal */}
      {showIgnoreModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg max-w-md w-full mx-4 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Ignore Suggestion</h3>
            <p className="text-gray-300 mb-6">
              Are you sure you want to ignore this suggestion and delete it? This action cannot be undone.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => handleIgnoreSuggestion(showIgnoreModal)}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                Confirm Delete
              </button>
              <button
                onClick={() => setShowIgnoreModal(null)}
                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              >
                Cancel Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-2">Support Tickets</h3>
          <p className="text-3xl font-bold text-red-400">{stats.total_tickets || 0}</p>
          <p className="text-sm text-gray-400">Total tickets received</p>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-2">AI Analyzed</h3>
          <p className="text-3xl font-bold text-green-400">{stats.analyzed_tickets || 0}</p>
          <p className="text-sm text-gray-400">Tickets analyzed by AI</p>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-2">Pending Actions</h3>
          <p className="text-3xl font-bold text-yellow-400">{stats.pending_suggestions || 0}</p>
          <p className="text-sm text-gray-400">Article suggestions awaiting review</p>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setSelectedPriority('all')}
          className={`px-4 py-2 rounded-lg font-medium ${
            selectedPriority === 'all' 
              ? 'bg-red-600 text-white' 
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          All Suggestions ({getAllSuggestions().length})
        </button>
        {['urgent', 'high', 'medium', 'low'].map(priority => (
          <button
            key={priority}
            onClick={() => setSelectedPriority(priority)}
            className={`px-4 py-2 rounded-lg font-medium capitalize ${
              selectedPriority === priority
                ? 'bg-red-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {priority} ({analysisData?.[priority]?.length || 0})
          </button>
        ))}
      </div>

      {/* Article Suggestions */}
      <div className="space-y-6">
        {getFilteredSuggestions().map((suggestion, index) => (
          <div key={index} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(suggestion.priority)}`}>
                    {suggestion.priority.toUpperCase()}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    suggestion.suggestion_type === 'existing_article' 
                      ? 'bg-green-900/20 text-green-400' 
                      : suggestion.suggestion_type === 'new_article' 
                      ? 'bg-blue-900/20 text-blue-400' 
                      : 'bg-purple-900/20 text-purple-400'
                  }`}>
                    {suggestion.suggestion_type === 'existing_article' ? 'EXISTING ARTICLE' : 
                     suggestion.suggestion_type === 'new_article' ? 'NEW ARTICLE' : 'UPDATE EXISTING'}
                  </span>
                  {suggestion.match_confidence && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      suggestion.match_confidence === 'high' 
                        ? 'bg-green-900/20 text-green-300' 
                        : 'bg-yellow-900/20 text-yellow-300'
                    }`}>
                      {suggestion.match_confidence.toUpperCase()} MATCH
                    </span>
                  )}
                  {suggestion.relevance_score && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-900/20 text-blue-300">
                      {suggestion.relevance_score}/10 RELEVANCE
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{suggestion.title}</h3>
                <p className="text-gray-300 mb-4">{suggestion.reasoning}</p>
              </div>
              
              {/* Action Buttons - Different for existing vs new articles */}
              <div className="flex gap-2">
                {suggestion.suggestion_type === 'existing_article' ? (
                  // Existing Article Buttons
                  <>
                    <button
                      onClick={() => handleExistingArticleFeedback(suggestion, 'good')}
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 font-medium"
                    >
                      ✓ Good Match
                    </button>
                    <button
                      onClick={() => handleExistingArticleFeedback(suggestion, 'not_relevant')}
                      className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 font-medium"
                    >
                      ✗ Not Relevant
                    </button>
                  </>
                ) : (
                  // New Article Buttons
                  <>
                    <button
                      onClick={() => handleCreateArticle(suggestion)}
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 font-medium"
                    >
                      ✓ Create Article
                    </button>
                    <button
                      onClick={() => setShowIgnoreModal(suggestion)}
                      className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 font-medium"
                    >
                      ✗ Ignore
                    </button>
                  </>
                )}
              </div>
            </div>
            
            {/* Existing Article Links */}
            {suggestion.suggestion_type === 'existing_article' && (
              <div className="bg-blue-900/20 border border-blue-700 p-4 rounded-lg mb-4">
                <h4 className="text-sm font-medium text-blue-300 mb-2">📄 Existing Article Links:</h4>
                <div className="flex gap-4">
                  <a 
                    href={`#article-${suggestion.article_id}`}
                    onClick={() => onViewChange(`article-${suggestion.article_id}`)}
                    className="text-blue-300 hover:text-blue-200 text-sm underline"
                  >
                    → View Public Article
                  </a>
                  <a 
                    href={`#admin-article-${suggestion.article_id}`}
                    onClick={() => onViewChange(`admin-article-${suggestion.article_id}-edit`)}
                    className="text-blue-300 hover:text-blue-200 text-sm underline"
                  >
                    → Edit Article
                  </a>
                </div>
                {suggestion.category && (
                  <p className="text-xs text-blue-200 mt-2">Category: {suggestion.category}</p>
                )}
              </div>
            )}
            
            {/* Content Preview */}
            <div className="bg-gray-900 p-4 rounded-lg mb-4">
              <h4 className="text-sm font-medium text-gray-300 mb-2">
                {suggestion.suggestion_type === 'existing_article' ? 'Article Content Preview:' : 'Suggested Content Preview:'}
              </h4>
              <div 
                className="text-sm text-gray-300 max-h-60 overflow-y-auto"
                dangerouslySetInnerHTML={{ 
                  __html: suggestion.content_preview || suggestion.content || 'Content not available'
                }}
              />
            </div>

            {/* Training Feedback */}
            {suggestion.training_feedback && (
              <div className="bg-blue-900/20 border border-blue-700 p-4 rounded-lg mb-4">
                <h4 className="text-sm font-medium text-blue-300 mb-2">💡 AI Training Feedback:</h4>
                <p className="text-sm text-blue-200">{suggestion.training_feedback}</p>
              </div>
            )}

            {/* Missing Info (for article outlines) */}
            {suggestion.missing_info && (
              <div className="bg-yellow-900/20 border border-yellow-700 p-4 rounded-lg mb-4">
                <h4 className="text-sm font-medium text-yellow-300 mb-2">⚠️ Missing Information:</h4>
                <p className="text-sm text-yellow-200">{suggestion.missing_info}</p>
              </div>
            )}
            
            {/* Tags */}
            {suggestion.tags && suggestion.tags.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-300 mb-2">
                  {suggestion.suggestion_type === 'existing_article' ? 'Article Tags:' : 'Suggested Tags:'}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {suggestion.tags.map((tag, tagIndex) => (
                    <span key={tagIndex} className="bg-gray-700 text-gray-300 px-2 py-1 rounded-full text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
        
        {getFilteredSuggestions().length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🤖</div>
            <h3 className="text-xl font-semibold text-white mb-2">No AI Suggestions Yet</h3>
            <p className="text-gray-400">
              {selectedPriority === 'all' 
                ? 'AI will analyze support tickets and suggest articles as they come in.'
                : `No ${selectedPriority} priority suggestions at the moment.`
              }
            </p>
          </div>
        )}
      </div>

      {/* Recent Tickets Section */}
      {recentTickets.length > 0 && (
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-white mb-6">Recent Support Tickets</h2>
          <div className="space-y-4">
            {recentTickets.slice(0, 5).map((ticket) => (
              <div 
                key={ticket.id} 
                className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-gray-600 cursor-pointer transition-colors"
                onClick={() => handleTicketClick(ticket)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm font-medium text-white">
                        {ticket.first_name} {ticket.last_name}
                      </span>
                      <span className="text-xs text-gray-400">
                        {ticket.business_name && `• ${ticket.business_name}`}
                      </span>
                      <span className="text-xs text-gray-400">
                        {ticket.timestamp && new Date(ticket.timestamp).toLocaleDateString()}
                      </span>
                      <span className="text-xs text-blue-400 hover:text-blue-300">
                        👁️ Click to view AI analysis
                      </span>
                    </div>
                    <p className="text-sm text-gray-300 mb-2">{ticket.subject}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      <span>Category: {ticket.category}</span>
                      <span>Urgency: {ticket.urgency}</span>
                      <span className={`px-2 py-1 rounded-full ${
                        ticket.analyzed ? 'bg-green-900/20 text-green-400' : 'bg-yellow-900/20 text-yellow-400'
                      }`}>
                        {ticket.analyzed ? 'Analyzed' : 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Train AI Component
function getDefaultVideoTitle() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const year = String(d.getFullYear()).slice(-2);
  return `Support Call ${month}/${day}/${year}`;
}

function TrainAI({ token, onLogout, onViewChange }) {
  const [trainingData, setTrainingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState(getDefaultVideoTitle());
  const [videoDescription, setVideoDescription] = useState('');
  const [videoPublished, setVideoPublished] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadTrainingData();
  }, []);

  const loadTrainingData = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/ai-training`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTrainingData(data.training_data);
      }
    } catch (error) {
      console.error('Failed to load training data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_BASE}/api/ai-training/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        showMessage('Training file uploaded successfully!', 'success');
        loadTrainingData();
      } else {
        const errorData = await response.json();
        showMessage(errorData.detail || 'Failed to upload file', 'error');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      showMessage('Error uploading file', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleAddUrl = async () => {
    if (!newUrl.trim()) return;

    try {
      const response = await fetch(`${API_BASE}/api/ai-training/url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url: newUrl })
      });

      if (response.ok) {
        showMessage('Training URL added successfully!', 'success');
        setNewUrl('');
        loadTrainingData();
      } else {
        const errorData = await response.json();
        showMessage(errorData.detail || 'Failed to add URL', 'error');
      }
    } catch (error) {
      console.error('Error adding URL:', error);
      showMessage('Error adding URL', 'error');
    }
  };

  const handleDeleteTraining = async (trainingId) => {
    if (window.confirm('Are you sure you want to delete this training data?')) {
      try {
        const response = await fetch(`${API_BASE}/api/ai-training/${trainingId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          showMessage('Training data deleted successfully!', 'success');
          loadTrainingData();
        } else {
          showMessage('Failed to delete training data', 'error');
        }
      } catch (error) {
        console.error('Error deleting training data:', error);
        showMessage('Error deleting training data', 'error');
      }
    }
  };

  const handleAddVideo = async () => {
    if (!videoUrl.trim()) return;

    try {
      const response = await fetch(`${API_BASE}/api/ai-training/video`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          video_url: videoUrl,
          title: videoTitle,
          description: videoDescription,
          published: videoPublished
        })
      });

      if (response.ok) {
        showMessage('Support call video added successfully!', 'success');
        setVideoUrl('');
        setVideoTitle(getDefaultVideoTitle());
        setVideoDescription('');
        setVideoPublished(true);
        loadTrainingData();
      } else {
        const errorData = await response.json();
        showMessage(errorData.detail || 'Failed to add video', 'error');
      }
    } catch (error) {
      console.error('Error adding video:', error);
      showMessage('Error adding video', 'error');
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(''), 3000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <p className="mt-4 text-gray-300">Loading training data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Toast Message */}
      {message && (
        <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
          message.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
        }`}>
          {message.text}
        </div>
      )}

      <div className="max-w-4xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white">Train AI</h1>
          <div className="space-x-4">
            <button
              onClick={() => onViewChange('admin-dashboard')}
              className="text-gray-400 hover:text-gray-200"
            >
              ← Back to Dashboard
            </button>
            <button
              onClick={onLogout}
              className="text-red-400 hover:text-red-200"
            >
              Logout
            </button>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700">
          <h2 className="text-xl font-semibold text-white mb-4">Upload Training Files and Links</h2>
          <p className="text-gray-300 mb-4">
            Upload text files to help train the AI on your specific content, processes, and terminology.
          </p>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Upload Text File (.txt)
              </label>
              <input
                type="file"
                accept=".txt"
                onChange={handleFileUpload}
                disabled={uploading}
                className="block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-red-600 file:text-white hover:file:bg-red-700"
              />
              {uploading && <p className="text-sm text-red-400 mt-2">Uploading...</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Add Training URL
              </label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  placeholder="https://example.com/training-document"
                  className="flex-1 px-3 py-2 border border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white placeholder-gray-400"
                />
                <button
                  onClick={handleAddUrl}
                  disabled={!newUrl.trim()}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                >
                  Add URL
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700">
          <h2 className="text-xl font-semibold text-white mb-4">Add Link to Support Call Video</h2>

          <div className="space-y-4">
            <div>
              <div className="space-y-3">
                <input
                  type="url"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://drive.google.com/file/d/..."
                  className="w-full px-3 py-2 border border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white placeholder-gray-400"
                />
                <input
                  type="text"
                  value={videoTitle}
                  onChange={(e) => setVideoTitle(e.target.value)}
                  placeholder="Video title (optional)"
                  className="w-full px-3 py-2 border border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white placeholder-gray-400"
                />
                <textarea
                  value={videoDescription}
                  onChange={(e) => setVideoDescription(e.target.value)}
                  placeholder="Video description (optional)"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white placeholder-gray-400"
                />
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="video-published"
                    checked={videoPublished}
                    onChange={(e) => setVideoPublished(e.target.checked)}
                    className="w-4 h-4 text-red-600 bg-gray-700 border-gray-600 rounded focus:ring-red-500"
                  />
                  <label htmlFor="video-published" className="text-sm text-gray-300">
                    Publish video (make available on public site)
                  </label>
                </div>
                <button
                  onClick={handleAddVideo}
                  disabled={!videoUrl.trim()}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                >
                  Add Support Call Video
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg border border-gray-700">
          <div className="p-6 border-b border-gray-700">
            <h2 className="text-xl font-semibold text-white">Training Data ({trainingData.length})</h2>
          </div>
          
          <div className="divide-y divide-gray-700">
            {trainingData.length === 0 ? (
              <div className="p-6 text-center">
                <div className="text-6xl mb-4">📚</div>
                <h3 className="text-xl font-semibold text-white mb-2">No Training Data Yet</h3>
                <p className="text-gray-400">
                  Upload text files or add URLs to help train the AI on your specific content.
                </p>
              </div>
            ) : (
              trainingData.map((training) => (
                <div key={training.id} className="p-6 hover:bg-gray-700">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          training.type === 'file' 
                            ? 'bg-blue-900/20 text-blue-400' 
                            : 'bg-green-900/20 text-green-400'
                        }`}>
                          {training.type === 'file' ? 'FILE' : 'URL'}
                        </span>
                        <span className="text-sm text-gray-400">
                          {new Date(training.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <h3 className="text-lg font-medium text-white mb-2">
                        {training.type === 'file' ? training.filename : training.url}
                      </h3>
                      <div className="bg-gray-900 p-3 rounded-lg">
                        <p className="text-sm text-gray-300 line-clamp-3">
                          {training.content.substring(0, 200)}...
                        </p>
                      </div>
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() => handleDeleteTraining(training.id)}
                        className="text-red-400 hover:text-red-200 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Videos Management Tab Component
function VideosTab({ token, onViewChange }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [editingVideo, setEditingVideo] = useState(null);
  const [editForm, setEditForm] = useState({ video_url: '', title: '', description: '', published: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/videos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVideos(data.videos);
      }
    } catch (error) {
      console.error('Failed to load videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (videoId, published) => {
    try {
      const response = await fetch(`${API_BASE}/api/videos/${videoId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ published })
      });

      if (response.ok) {
        showMessage(`Video ${published ? 'published' : 'unpublished'} successfully!`, 'success');
        loadVideos();
      } else {
        showMessage('Failed to update video', 'error');
      }
    } catch (error) {
      console.error('Error updating video:', error);
      showMessage('Error updating video', 'error');
    }
  };

  const handleDelete = async (videoId) => {
    if (window.confirm('Are you sure you want to delete this video? This action cannot be undone.')) {
      try {
        const response = await fetch(`${API_BASE}/api/videos/${videoId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          showMessage('Video deleted successfully!', 'success');
          loadVideos();
        } else {
          showMessage('Failed to delete video', 'error');
        }
      } catch (error) {
        console.error('Error deleting video:', error);
        showMessage('Error deleting video', 'error');
      }
    }
  };

  const handleCopyPublicLink = (videoId) => {
    const publicUrl = `${window.location.origin}/#video-${videoId}`;
    navigator.clipboard.writeText(publicUrl).then(() => {
      showMessage('Public link copied to clipboard!', 'success');
    });
  };

  const openEditModal = (video) => {
    setEditForm({
      video_url: video.video_url || '',
      title: video.title || '',
      description: video.description || '',
      published: !!video.published
    });
    setEditingVideo(video);
  };

  const closeEditModal = () => {
    setEditingVideo(null);
  };

  const handleSaveEdit = async () => {
    if (!editingVideo) return;
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/videos/${editingVideo.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          video_url: editForm.video_url,
          title: editForm.title,
          description: editForm.description,
          published: editForm.published
        })
      });

      if (response.ok) {
        showMessage('Video updated successfully!', 'success');
        closeEditModal();
        loadVideos();
      } else {
        const errorData = await response.json().catch(() => ({}));
        showMessage(errorData.detail || 'Failed to update video', 'error');
      }
    } catch (error) {
      console.error('Error updating video:', error);
      showMessage('Error updating video', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(''), 3000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <p className="mt-4 text-gray-300">Loading videos...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Toast Message */}
      {message && (
        <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
          message.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
        }`}>
          {message.text}
        </div>
      )}

      <div className="space-y-6">
        {videos.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎥</div>
            <h3 className="text-xl font-semibold text-white mb-2">No Support Call Videos Yet</h3>
            <p className="text-gray-400">
              Add support call videos in the Train AI tab to get started.
            </p>
          </div>
        ) : (
          videos.map((video) => (
            <div key={video.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      video.published 
                        ? 'bg-green-900/20 text-green-400' 
                        : 'bg-gray-900/20 text-gray-400'
                    }`}>
                      {video.published ? 'PUBLISHED' : 'DRAFT'}
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(video.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{video.title}</h3>
                  {video.description && (
                    <p className="text-gray-300 mb-4">{video.description}</p>
                  )}
                  <div className="bg-gray-900 p-3 rounded-lg">
                    <p className="text-sm text-gray-300 line-clamp-3">
                      {video.content ? video.content.substring(0, 200) + '...' : 'Processing transcript...'}
                    </p>
                  </div>
                </div>
                <div className="ml-4 flex flex-col gap-2">
                  {video.published && (
                    <button
                      onClick={() => handleCopyPublicLink(video.id)}
                      className="text-blue-400 hover:text-blue-200 text-sm"
                      data-testid={`copy-public-link-btn-${video.id}`}
                    >
                      Copy Public Link
                    </button>
                  )}
                  <button
                    onClick={() => openEditModal(video)}
                    className="text-gray-300 hover:text-white text-sm"
                    data-testid={`edit-video-btn-${video.id}`}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handlePublish(video.id, !video.published)}
                    className={`text-sm ${
                      video.published 
                        ? 'text-yellow-400 hover:text-yellow-200' 
                        : 'text-green-400 hover:text-green-200'
                    }`}
                    data-testid={`toggle-publish-btn-${video.id}`}
                  >
                    {video.published ? 'Unpublish' : 'Publish'}
                  </button>
                  <button
                    onClick={() => handleDelete(video.id)}
                    className="text-red-400 hover:text-red-200 text-sm"
                    data-testid={`delete-video-btn-${video.id}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Edit Video Modal */}
      {editingVideo && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          data-testid="edit-video-modal"
        >
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-white">Edit Support Call Video</h3>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-white text-2xl leading-none"
                data-testid="edit-video-close-btn"
              >
                &times;
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Video Link</label>
                <input
                  type="url"
                  value={editForm.video_url}
                  onChange={(e) => setEditForm({ ...editForm, video_url: e.target.value })}
                  placeholder="https://drive.google.com/file/d/..."
                  className="w-full px-3 py-2 border border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white placeholder-gray-400"
                  data-testid="edit-video-url-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  placeholder="Video title"
                  className="w-full px-3 py-2 border border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white placeholder-gray-400"
                  data-testid="edit-video-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder="Video description"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white placeholder-gray-400"
                  data-testid="edit-video-description-input"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-video-published"
                  checked={editForm.published}
                  onChange={(e) => setEditForm({ ...editForm, published: e.target.checked })}
                  className="w-4 h-4 text-red-600 bg-gray-700 border-gray-600 rounded focus:ring-red-500"
                  data-testid="edit-video-published-checkbox"
                />
                <label htmlFor="edit-video-published" className="text-sm text-gray-300">
                  Publish video (make available on public site)
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={closeEditModal}
                  className="px-4 py-2 rounded text-gray-300 hover:text-white border border-gray-600"
                  data-testid="edit-video-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={saving || !editForm.video_url.trim()}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                  data-testid="edit-video-save-btn"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Public Videos Page Component
function PublicVideos({ onViewChange }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [videosPerPage, setVideosPerPage] = useState(10);
  const [showAllVideos, setShowAllVideos] = useState(false);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadVideos();
  }, [currentPage, videosPerPage, showAllVideos]);

  const loadVideos = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * videosPerPage;
      const days = showAllVideos ? 0 : 30;
      
      const response = await fetch(
        `${API_BASE}/api/public/videos?skip=${skip}&limit=${videosPerPage}&days=${days}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setVideos(data.videos);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Failed to load videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / videosPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleVideosPerPageChange = (newPerPage) => {
    setVideosPerPage(newPerPage);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Support Call Archives</h1>
            <p className="text-gray-300">
              {showAllVideos 
                ? 'Browse all support call videos' 
                : 'Recent support call videos from the last 30 days'
              }
            </p>
          </div>
          <button
            onClick={() => onViewChange('public')}
            className="text-red-400 hover:text-red-200"
          >
            ← Back to Help Center
          </button>
        </div>

        <div className="mb-6 flex flex-wrap gap-4 items-center">
          <button
            onClick={() => {
              setShowAllVideos(!showAllVideos);
              setCurrentPage(1);
            }}
            className={`px-4 py-2 rounded-lg font-medium ${
              showAllVideos 
                ? 'bg-red-600 text-white' 
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {showAllVideos ? 'Show Recent (30 days)' : 'Show All Videos'}
          </button>

          {videos.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-400">Videos per page:</span>
              <select
                value={videosPerPage}
                onChange={(e) => handleVideosPerPageChange(parseInt(e.target.value))}
                className="px-3 py-1 border border-gray-600 rounded text-sm focus:ring-2 focus:ring-red-500 bg-gray-800 text-white"
              >
                <option value={10}>10</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            <p className="mt-4 text-gray-300">Loading videos...</p>
          </div>
        ) : (
          <>
            {videos.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🎥</div>
                <h3 className="text-xl font-semibold text-white mb-2">No Videos Available</h3>
                <p className="text-gray-400">
                  {showAllVideos 
                    ? 'No support call videos have been published yet.' 
                    : 'No support call videos from the last 30 days.'
                  }
                </p>
              </div>
            ) : (
              <>
                <div className="grid gap-6 mb-8">
                  {videos.map((video) => (
                    <div 
                      key={video.id} 
                      className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-gray-600 cursor-pointer transition-colors"
                      onClick={() => onViewChange(`video-${video.id}`)}
                    >
                      <div className="flex items-start gap-4">
                        {video.thumbnail_url && (
                          <img 
                            src={video.thumbnail_url} 
                            alt={video.title}
                            className="w-32 h-20 object-cover rounded"
                          />
                        )}
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-white mb-2">{video.title}</h3>
                          {video.description && (
                            <p className="text-gray-300 mb-3">{video.description}</p>
                          )}
                          <p className="text-gray-400 text-sm mb-2">{video.excerpt}</p>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>{new Date(video.created_at).toLocaleDateString()}</span>
                            <span>Support Call Video</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-2 bg-gray-700 text-white rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-2 rounded ${
                            currentPage === page
                              ? 'bg-red-600 text-white'
                              : 'bg-gray-700 text-white hover:bg-gray-600'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 bg-gray-700 text-white rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}

                <div className="text-center mt-4 text-sm text-gray-400">
                  Showing {((currentPage - 1) * videosPerPage) + 1} to {Math.min(currentPage * videosPerPage, total)} of {total} videos
                </div>
              </>
            )}
          </>
        )}

        {/* Admin login moved to bottom */}
        <div className="fixed bottom-4 right-4">
          <button
            onClick={() => onViewChange('admin-login')}
            className="text-red-400 hover:text-red-200 text-sm bg-gray-800 px-3 py-2 rounded border border-gray-600"
          >
            Admin Login
          </button>
        </div>
      </div>
    </div>
  );
}

// Video Detail Page Component
function VideoDetail({ videoId, onViewChange }) {
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadVideo();
  }, [videoId]);

  const loadVideo = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/public/videos/${videoId}`);
      if (response.ok) {
        const data = await response.json();
        setVideo(data);
      } else {
        // Video not found or not published
        setVideo(null);
      }
    } catch (error) {
      console.error('Failed to load video:', error);
      setVideo(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <p className="mt-4 text-gray-300">Loading video...</p>
        </div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-black">
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold text-white mb-4">Video Not Found</h1>
            <p className="text-gray-300 mb-6">The requested video could not be found or is not available.</p>
            <button
              onClick={() => onViewChange('public-videos')}
              className="text-red-400 hover:text-red-200"
            >
              ← Back to Video Archives
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-5xl mx-auto px-4 pt-6">
        <div className="mb-6">
          <button
            onClick={() => onViewChange('public-videos')}
            className="text-red-400 hover:text-red-200 mb-4"
          >
            ← Back to Video Archives
          </button>
          <h1 className="text-3xl font-bold text-white mb-2">{video.title}</h1>
          {video.description && (
            <p className="text-gray-300 mb-4">{video.description}</p>
          )}
          <div className="text-sm text-gray-400">
            Published on {new Date(video.created_at).toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* Video Player - nearly full width */}
      {video.embed_url && (
        <div className="w-full px-4 sm:px-6 pb-12">
          <div className="relative w-full max-w-[1800px] mx-auto" style={{ paddingTop: '56.25%' }}>
            <iframe
              src={video.embed_url}
              className="absolute top-0 left-0 w-full h-full rounded-lg"
              frameBorder="0"
              allowFullScreen
              title={video.title}
            />
          </div>
        </div>
      )}

      {/* Admin login moved to bottom */}
      <div className="fixed bottom-4 right-4">
        <button
          onClick={() => onViewChange('admin-login')}
          className="text-red-400 hover:text-red-200 text-sm bg-gray-800 px-3 py-2 rounded border border-gray-600"
        >
          Admin Login
        </button>
      </div>
    </div>
  );
}

export default App;

// Articles Tab Component
function ArticlesTab({ articles, loading, onEditArticle, onNewArticle, onCopyPublicLink, onCopyAdminLink, token, onReload }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [copyMessage, setCopyMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleTogglePublish = async (articleId, currentStatus) => {
    try {
      const response = await fetch(`${API_BASE}/api/articles/${articleId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ published: !currentStatus })
      });
      
      if (response.ok) {
        onReload();
      }
    } catch (error) {
      console.error('Failed to toggle publish status:', error);
    }
  };

  const handleDeleteArticle = async (articleId) => {
    try {
      const response = await fetch(`${API_BASE}/api/articles/${articleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        onReload();
        showCopyMessage('Article deleted successfully!');
      } else {
        showCopyMessage('Failed to delete article', 'error');
      }
    } catch (error) {
      console.error('Failed to delete article:', error);
      showCopyMessage('Error deleting article', 'error');
    }
    setDeleteConfirm(null);
  };

  const showCopyMessage = (message, type = 'success') => {
    setCopyMessage({ text: message, type });
    setTimeout(() => setCopyMessage(''), 3000);
  };

  const handleCopyPublicLink = (articleId) => {
    const publicUrl = `${window.location.origin}/article/${articleId}`;
    navigator.clipboard.writeText(publicUrl).then(() => {
      showCopyMessage('Public link copied to clipboard!');
    });
  };

  const handleCopyAdminLink = (articleId) => {
    const adminUrl = `${window.location.origin}/admin/edit/${articleId}`;
    navigator.clipboard.writeText(adminUrl).then(() => {
      showCopyMessage('Admin link copied to clipboard!');
    });
  };

  // Filter articles based on search query
  const filteredArticles = articles.filter(article => 
    article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (article.tags && article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))) ||
    (article.category_name && article.category_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
        <p className="mt-4 text-gray-300">Loading articles...</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg shadow">
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg max-w-md w-full mx-4 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Confirm Delete</h3>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete the article "{deleteConfirm.title}"? This action cannot be undone.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => handleDeleteArticle(deleteConfirm.id)}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                Confirm Delete
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              >
                Cancel Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Message */}
      {copyMessage && (
        <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
          copyMessage.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
        }`}>
          {copyMessage.text}
        </div>
      )}

      <div className="p-6 border-b border-gray-700">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-white">Articles ({filteredArticles.length} of {articles.length})</h2>
          <button
            onClick={onNewArticle}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            New Article
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search articles by title, content, tags, or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white placeholder-gray-400"
          />
        </div>
      </div>

      <div className="divide-y divide-gray-700">
        {filteredArticles.map(article => (
          <div key={article.id} className="p-6 hover:bg-gray-700">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="text-lg font-medium text-white mb-2">{article.title}</h3>
                <p className="text-gray-300 mb-3">
                  {article.content.replace(/<[^>]*>/g, '').substring(0, 150)}...
                </p>
                <div className="flex items-center space-x-4 text-sm text-gray-400 mb-3">
                  <span>Created: {new Date(article.created_at).toLocaleDateString()}</span>
                  {article.category_name && <span>Category: {article.category_name}</span>}
                  {article.tags && article.tags.length > 0 && (
                    <span>Tags: {article.tags.join(', ')}</span>
                  )}
                </div>
                
                {/* Copy Links */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleCopyPublicLink(article.id)}
                    className="bg-green-800 text-green-200 px-3 py-1 rounded text-sm hover:bg-green-700"
                  >
                    📋 Copy Public Link
                  </button>
                  <button
                    onClick={() => handleCopyAdminLink(article.id)}
                    className="bg-purple-800 text-purple-200 px-3 py-1 rounded text-sm hover:bg-purple-700"
                  >
                    📋 Copy Admin Link
                  </button>
                </div>
              </div>
              
              <div className="flex flex-col items-end space-y-2">                
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleTogglePublish(article.id, article.published)}
                    className={`px-3 py-1 rounded text-sm ${
                      article.published
                        ? 'bg-yellow-800 text-yellow-200 hover:bg-yellow-700'
                        : 'bg-green-800 text-green-200 hover:bg-green-700'
                    }`}
                  >
                    {article.published ? 'Unpublish' : 'Publish'}
                  </button>
                  <button
                    onClick={() => onEditArticle(article)}
                    className="bg-blue-800 text-blue-200 px-3 py-1 rounded text-sm hover:bg-blue-700"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setDeleteConfirm({ id: article.id, title: article.title })}
                    className="bg-red-800 text-red-200 px-3 py-1 rounded text-sm hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {filteredArticles.length === 0 && searchQuery && (
          <div className="p-12 text-center">
            <p className="text-gray-400">No articles found matching "{searchQuery}"</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Admin Edit Article Component (separate page)
function AdminEditArticle({ articleId, token, onLogout, onViewChange }) {
  const [article, setArticle] = useState(null);
  const [categories, setCategories] = useState([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [tags, setTags] = useState('');
  const [published, setPublished] = useState(false);
  const [featured, setFeatured] = useState(false);
  const [mediaUrls, setMediaUrls] = useState([]);
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [loading, setLoading] = useState(articleId !== 'new');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  // Track original values for comparison
  const [originalValues, setOriginalValues] = useState({});

  useEffect(() => {
    loadCategories();
    if (articleId !== 'new') {
      loadArticle();
    }
  }, [articleId]);

  // Track changes
  useEffect(() => {
    if (originalValues.title !== undefined) {
      const hasChanges = 
        title !== originalValues.title ||
        content !== originalValues.content ||
        categoryId !== originalValues.categoryId ||
        tags !== originalValues.tags ||
        published !== originalValues.published ||
        featured !== originalValues.featured ||
        JSON.stringify(mediaUrls) !== JSON.stringify(originalValues.mediaUrls);
      
      setHasUnsavedChanges(hasChanges);
    }
  }, [title, content, categoryId, tags, published, featured, mediaUrls, originalValues]);

  // Handle browser navigation/tab close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    const handlePopState = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        const confirmNavigation = window.confirm('You have unsaved changes. Are you sure you want to leave this page?');
        if (!confirmNavigation) {
          // Push the state back to prevent navigation
          window.history.pushState({}, '', window.location.pathname);
          return;
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges]);

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setCategories(await response.json());
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadArticle = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/articles/${articleId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const articleData = await response.json();
        setArticle(articleData);
        
        const values = {
          title: articleData.title || '',
          content: articleData.content || '',
          categoryId: articleData.category_id || '',
          tags: articleData.tags ? articleData.tags.join(', ') : '',
          published: articleData.published || false,
          featured: articleData.featured || false,
          mediaUrls: articleData.media_urls || []
        };
        
        setTitle(values.title);
        setContent(values.content);
        setCategoryId(values.categoryId);
        setTags(values.tags);
        setPublished(values.published);
        setFeatured(values.featured);
        setMediaUrls(values.mediaUrls);
        setOriginalValues(values);
      }
    } catch (error) {
      console.error('Failed to load article:', error);
    } finally {
      setLoading(false);
    }
  };

  const [copyMessage, setCopyMessage] = useState('');

  const handleSave = async () => {
    setSaving(true);
    
    const articleData = {
      title,
      content,
      category_id: categoryId || null,
      tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag),
      published,
      featured,
      media_urls: mediaUrls
    };

    try {
      const url = articleId !== 'new' 
        ? `${API_BASE}/api/articles/${articleId}`
        : `${API_BASE}/api/articles`;
      
      const method = articleId !== 'new' ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(articleData)
      });

      if (response.ok) {
        if (articleId === 'new') {
          // For new articles, get the created article ID and redirect to edit it
          const newArticle = await response.json();
          onViewChange('admin-edit', newArticle.id);
          window.history.pushState({}, '', `/admin/edit/${newArticle.id}`);
        } else {
          // For existing articles, just reload the current article
          loadArticle();
        }
        
        // Reset unsaved changes flag
        setHasUnsavedChanges(false);
        
        // Show success message
        showCopyMessage('Article saved successfully!', 'success');
      } else {
        showCopyMessage('Failed to save article', 'error');
      }
    } catch (error) {
      console.error('Error saving article:', error);
      showCopyMessage('Error saving article', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleNavigateWithWarning = (navigationFn) => {
    if (hasUnsavedChanges) {
      setShowUnsavedModal(true);
      // Store navigation function for later use
      window.pendingNavigation = navigationFn;
    } else {
      navigationFn();
    }
  };

  const handleSaveAndNavigate = async () => {
    await handleSave();
    setShowUnsavedModal(false);
    if (window.pendingNavigation) {
      window.pendingNavigation();
      window.pendingNavigation = null;
    }
  };

  const handleDiscardAndNavigate = () => {
    setHasUnsavedChanges(false);
    setShowUnsavedModal(false);
    if (window.pendingNavigation) {
      window.pendingNavigation();
      window.pendingNavigation = null;
    }
  };

  const showCopyMessage = (message, type = 'success') => {
    setCopyMessage({ text: message, type });
    setTimeout(() => setCopyMessage(''), 3000);
  };

  const handleCopyPublicLink = () => {
    if (articleId !== 'new') {
      const publicUrl = `${window.location.origin}/article/${articleId}`;
      navigator.clipboard.writeText(publicUrl).then(() => {
        showCopyMessage('Public link copied to clipboard!');
      });
    }
  };

  const handleCopyAdminLink = () => {
    if (articleId !== 'new') {
      const adminUrl = `${window.location.origin}/admin/edit/${articleId}`;
      navigator.clipboard.writeText(adminUrl).then(() => {
        showCopyMessage('Admin link copied to clipboard!');
      });
    }
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingVideo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_BASE}/api/upload/video`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setMediaUrls([...mediaUrls, data.file_path]);
      }
    } catch (error) {
      console.error('Error uploading video:', error);
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleAddVideoUrl = () => {
    if (newVideoUrl.trim()) {
      setMediaUrls([...mediaUrls, newVideoUrl.trim()]);
      setNewVideoUrl('');
    }
  };

  const handleRemoveMedia = (index) => {
    setMediaUrls(mediaUrls.filter((_, i) => i !== index));
  };

  // WYSIWYG Editor Functions
  const insertFormatting = (command, value = null) => {
    const editor = document.getElementById('visual-editor');
    if (editor) {
      editor.focus();
      document.execCommand(command, false, value);
      // Update content state
      setContent(editor.innerHTML);
    }
  };

  const handleVisualEditorChange = (e) => {
    // Get the content directly from the event target
    const newContent = e.target.innerHTML;
    setContent(newContent);
  };

  // Initialize visual editor content when switching modes
  useEffect(() => {
    if (!isHtmlMode) {
      const editor = document.getElementById('visual-editor');
      if (editor && editor.innerHTML !== content) {
        // Save cursor position
        const selection = window.getSelection();
        const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
        
        // Update content only if different
        if (editor.innerHTML !== content) {
          editor.innerHTML = content;
          
          // Restore cursor position if possible
          if (range) {
            try {
              selection.removeAllRanges();
              selection.addRange(range);
            } catch (e) {
              // Cursor restoration failed, place at end
              const newRange = document.createRange();
              newRange.selectNodeContents(editor);
              newRange.collapse(false);
              selection.removeAllRanges();
              selection.addRange(newRange);
            }
          }
        }
      }
    }
  }, [isHtmlMode, content]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <p className="mt-4 text-gray-300">Loading article...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Unsaved Changes Modal */}
      {showUnsavedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg max-w-md w-full mx-4 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Unsaved Changes</h3>
            <p className="text-gray-300 mb-6">
              You have unsaved changes. What would you like to do?
            </p>
            <div className="flex space-x-4">
              <button
                onClick={handleSaveAndNavigate}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                Save Changes
              </button>
              <button
                onClick={handleDiscardAndNavigate}
                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              >
                Don't Save Changes
              </button>
              <button
                onClick={() => setShowUnsavedModal(false)}
                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Message */}
      {copyMessage && (
        <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
          copyMessage.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
        }`}>
          {copyMessage.text}
        </div>
      )}
      
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white">
            {articleId === 'new' ? 'New Article' : 'Edit Article'}
            {hasUnsavedChanges && <span className="text-red-500 ml-2">*</span>}
          </h1>
          <div className="space-x-4">
            <button
              onClick={() => handleNavigateWithWarning(() => onViewChange('admin-dashboard'))}
              className="text-gray-400 hover:text-gray-200"
            >
              ← Back to Dashboard
            </button>
            <button
              onClick={() => handleNavigateWithWarning(onLogout)}
              className="text-red-400 hover:text-red-200"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Copy Links Section (only for existing articles) */}
        {articleId !== 'new' && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-gray-300 mb-3">Share This Article</h3>
            <div className="flex space-x-3">
              <button
                onClick={handleCopyPublicLink}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm"
              >
                📋 Copy Public Link
              </button>
              <button
                onClick={handleCopyAdminLink}
                className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 text-sm"
              >
                📋 Copy Admin Link
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Public link for clients • Admin link for team members
            </p>
          </div>
        )}

        <div className="bg-gray-800 rounded-lg shadow p-6 border border-gray-700">
          {/* Title */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white placeholder-gray-400"
              placeholder="How do I manually schedule a lead?"
            />
          </div>

          {/* Category */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Category
            </label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white"
            >
              <option value="">Select a category</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Content Editor */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-300">
                Content
              </label>
              <button
                onClick={() => setIsHtmlMode(!isHtmlMode)}
                className="bg-gray-700 text-gray-300 px-3 py-1 rounded text-sm hover:bg-gray-600"
              >
                {isHtmlMode ? 'Visual Editor' : 'HTML Editor'}
              </button>
            </div>

            {/* Visual Editor (Editable) */}
            {!isHtmlMode ? (
              <div>
                <div className="mb-2 flex flex-wrap gap-2 p-2 bg-gray-700 rounded-t-lg border border-gray-600">
                  <button 
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertFormatting('formatBlock', '<p>')} 
                    className="px-2 py-1 bg-gray-600 rounded text-sm hover:bg-gray-500 text-white"
                  >
                    Normal
                  </button>
                  <button 
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertFormatting('bold')} 
                    className="px-2 py-1 bg-gray-600 rounded text-sm font-bold hover:bg-gray-500 text-white"
                  >
                    Bold
                  </button>
                  <button 
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertFormatting('italic')} 
                    className="px-2 py-1 bg-gray-600 rounded text-sm italic hover:bg-gray-500 text-white"
                  >
                    Italic
                  </button>
                  <button 
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertFormatting('formatBlock', '<h2>')} 
                    className="px-2 py-1 bg-gray-600 rounded text-sm hover:bg-gray-500 text-white"
                  >
                    H2
                  </button>
                  <button 
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertFormatting('formatBlock', '<h3>')} 
                    className="px-2 py-1 bg-gray-600 rounded text-sm hover:bg-gray-500 text-white"
                  >
                    H3
                  </button>
                  <button 
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertFormatting('insertUnorderedList')} 
                    className="px-2 py-1 bg-gray-600 rounded text-sm hover:bg-gray-500 text-white"
                  >
                    List
                  </button>
                  <button 
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      const editor = document.getElementById('visual-editor');
                      if (!editor) return;
                      
                      const url = prompt('Enter URL:');
                      if (!url) return;
                      
                      const openInNewTab = confirm('Open link in new tab?\n\nClick OK to open in new tab\nClick Cancel to open in same tab');
                      
                      editor.focus();
                      document.execCommand('createLink', false, url);
                      
                      // Find and modify the newly created link
                      if (openInNewTab) {
                        setTimeout(() => {
                          const links = editor.querySelectorAll('a[href="' + url + '"]');
                          if (links.length > 0) {
                            const lastLink = links[links.length - 1];
                            lastLink.setAttribute('target', '_blank');
                            lastLink.setAttribute('rel', 'noopener noreferrer');
                            setContent(editor.innerHTML);
                          }
                        }, 100);
                      } else {
                        setTimeout(() => {
                          setContent(editor.innerHTML);
                        }, 100);
                      }
                    }} 
                    className="px-2 py-1 bg-gray-600 rounded text-sm hover:bg-gray-500 text-white"
                  >
                    Link
                  </button>
                </div>
                
                {/* Editable Visual Area - Fixed Implementation */}
                <div
                  id="visual-editor"
                  contentEditable
                  suppressContentEditableWarning={true}
                  className="w-full p-4 border border-gray-600 rounded-b-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white min-h-[300px] prose max-w-none"
                  style={{ outline: 'none' }}
                  onInput={handleVisualEditorChange}
                  onBlur={handleVisualEditorChange}
                >
                  {/* Content is set via innerHTML in useEffect */}
                </div>
                <p className="text-sm text-gray-400 mt-1">
                  Visual Editor: Type directly and use toolbar buttons for formatting. Switch to HTML Editor for advanced formatting.
                </p>
              </div>
            ) : (
              <div>
                <textarea
                  id="content-editor"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={15}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white placeholder-gray-400 font-mono text-sm"
                  placeholder="Enter article content using HTML..."
                />
                <p className="text-sm text-gray-400 mt-1">
                  HTML Editor: Use HTML tags for formatting. Switch to Visual Editor for WYSIWYG editing.
                </p>
              </div>
            )}
          </div>

          {/* Media */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Media (Videos/Images)
            </label>
            
            <div className="bg-gray-700 border border-gray-600 rounded-lg p-4 mb-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Upload Video File
                </label>
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoUpload}
                  disabled={uploadingVideo}
                  className="block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-red-600 file:text-white hover:file:bg-red-700"
                />
                {uploadingVideo && <p className="text-sm text-red-400 mt-2">Uploading...</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Or Add Video URL
                </label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={newVideoUrl}
                    onChange={(e) => setNewVideoUrl(e.target.value)}
                    placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                    className="flex-1 px-3 py-2 border border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white placeholder-gray-400"
                  />
                  <button
                    onClick={handleAddVideoUrl}
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {mediaUrls.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-300">Media Files:</h4>
                {mediaUrls.map((url, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-700 p-2 rounded">
                    <span className="text-sm text-gray-300 truncate flex-1">{url}</span>
                    <button
                      onClick={() => handleRemoveMedia(index)}
                      className="text-red-400 hover:text-red-200 ml-2"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Tags
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white placeholder-gray-400"
              placeholder="tag1, tag2, tag3"
            />
          </div>

          {/* Options */}
          <div className="mb-6 space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={published}
                onChange={(e) => setPublished(e.target.checked)}
                className="rounded border-gray-600 text-red-600 focus:ring-red-500 bg-gray-700"
              />
              <span className="ml-2 text-sm text-gray-300">Published</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={featured}
                onChange={(e) => setFeatured(e.target.checked)}
                className="rounded border-gray-600 text-red-600 focus:ring-red-500 bg-gray-700"
              />
              <span className="ml-2 text-sm text-gray-300">Featured Article</span>
            </label>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving || !title.trim()}
              className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : (articleId === 'new' ? 'Create Article' : 'Update Article')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Admin Categories Component (separate page)
function AdminCategories({ token, onLogout, onViewChange }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setCategories(await response.json());
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCategory = async () => {
    const categoryData = { name: categoryName, description: categoryDescription };

    try {
      const url = editingCategory 
        ? `${API_BASE}/api/categories/${editingCategory.id}`
        : `${API_BASE}/api/categories`;
      
      const method = editingCategory ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(categoryData)
      });

      if (response.ok) {
        setShowNewCategory(false);
        setEditingCategory(null);
        setCategoryName('');
        setCategoryDescription('');
        loadCategories();
      }
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryName(category.name);
    setCategoryDescription(category.description);
    setShowNewCategory(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        const response = await fetch(`${API_BASE}/api/categories/${categoryId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          loadCategories();
        }
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <p className="mt-4 text-gray-300">Loading categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white">Manage Categories</h1>
          <div className="space-x-4">
            <button
              onClick={() => onViewChange('admin-dashboard')}
              className="text-gray-400 hover:text-gray-200"
            >
              ← Back to Dashboard
            </button>
            <button
              onClick={onLogout}
              className="text-red-400 hover:text-red-200"
            >
              Logout
            </button>
          </div>
        </div>

        {/* New/Edit Category Form */}
        {showNewCategory && (
          <div className="bg-gray-800 rounded-lg shadow p-6 mb-6 border border-gray-700">
            <h2 className="text-lg font-semibold text-white mb-4">
              {editingCategory ? 'Edit Category' : 'New Category'}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Name</label>
                <input
                  type="text"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-600 rounded focus:ring-2 focus:ring-red-500 bg-gray-700 text-white placeholder-gray-400"
                  placeholder="Category name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                <textarea
                  value={categoryDescription}
                  onChange={(e) => setCategoryDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-600 rounded focus:ring-2 focus:ring-red-500 bg-gray-700 text-white placeholder-gray-400"
                  placeholder="Category description"
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleSaveCategory}
                  disabled={!categoryName.trim()}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                >
                  {editingCategory ? 'Update' : 'Create'}
                </button>
                <button
                  onClick={() => {
                    setShowNewCategory(false);
                    setEditingCategory(null);
                    setCategoryName('');
                    setCategoryDescription('');
                  }}
                  className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Categories List */}
        <div className="bg-gray-800 rounded-lg shadow border border-gray-700">
          <div className="p-6 border-b border-gray-700 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white">Categories ({categories.length})</h2>
            <button
              onClick={() => {
                setShowNewCategory(true);
                setEditingCategory(null);
                setCategoryName('');
                setCategoryDescription('');
              }}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              New Category
            </button>
          </div>
          <div className="divide-y divide-gray-700">
            {categories.map(category => (
              <div key={category.id} className="p-6 hover:bg-gray-700">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-medium text-white">{category.name}</h3>
                    <p className="text-gray-300 mt-1">{category.description}</p>
                    <p className="text-sm text-gray-400 mt-2">
                      Created: {new Date(category.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditCategory(category)}
                      className="bg-blue-800 text-blue-200 px-3 py-1 rounded text-sm hover:bg-blue-700"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      className="bg-red-800 text-red-200 px-3 py-1 rounded text-sm hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Admin Users Component (separate page)
function AdminUsers({ token, onLogout, onViewChange }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewAdmin, setShowNewAdmin] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admins`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setAdmins(await response.json());
      }
    } catch (error) {
      console.error('Failed to load admins:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admins`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: adminEmail, password: adminPassword })
      });

      if (response.ok) {
        setShowNewAdmin(false);
        setAdminEmail('');
        setAdminPassword('');
        loadAdmins();
      }
    } catch (error) {
      console.error('Error creating admin:', error);
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (window.confirm('Are you sure you want to delete this admin?')) {
      try {
        const response = await fetch(`${API_BASE}/api/admins/${adminId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          loadAdmins();
        }
      } catch (error) {
        console.error('Error deleting admin:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <p className="mt-4 text-gray-300">Loading admin users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white">Manage Admin Users</h1>
          <div className="space-x-4">
            <button
              onClick={() => onViewChange('admin-dashboard')}
              className="text-gray-400 hover:text-gray-200"
            >
              ← Back to Dashboard
            </button>
            <button
              onClick={onLogout}
              className="text-red-400 hover:text-red-200"
            >
              Logout
            </button>
          </div>
        </div>

        {/* New Admin Form */}
        {showNewAdmin && (
          <div className="bg-gray-800 rounded-lg shadow p-6 mb-6 border border-gray-700">
            <h2 className="text-lg font-semibold text-white mb-4">New Admin User</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                <input
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-600 rounded focus:ring-2 focus:ring-red-500 bg-gray-700 text-white placeholder-gray-400"
                  placeholder="admin@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Password</label>
                <input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-600 rounded focus:ring-2 focus:ring-red-500 bg-gray-700 text-white placeholder-gray-400"
                  placeholder="Password"
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleCreateAdmin}
                  disabled={!adminEmail.trim() || !adminPassword.trim()}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                >
                  Create
                </button>
                <button
                  onClick={() => {
                    setShowNewAdmin(false);
                    setAdminEmail('');
                    setAdminPassword('');
                  }}
                  className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Admin Users List */}
        <div className="bg-gray-800 rounded-lg shadow border border-gray-700">
          <div className="p-6 border-b border-gray-700 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white">Admin Users ({admins.length})</h2>
            <button
              onClick={() => setShowNewAdmin(true)}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              New Admin
            </button>
          </div>
          <div className="divide-y divide-gray-700">
            {admins.map(admin => (
              <div key={admin.id} className="p-6 hover:bg-gray-700">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-medium text-white">{admin.email}</h3>
                    <p className="text-sm text-gray-400">
                      Created: {new Date(admin.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteAdmin(admin.id)}
                    className="bg-red-800 text-red-200 px-3 py-1 rounded text-sm hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Time conversion helper functions
function convertTo24Hour(time12) {
  const [time, modifier] = time12.split(' ');
  let [hours, minutes] = time.split(':');
  if (hours === '12') {
    hours = '00';
  }
  if (modifier === 'PM') {
    hours = parseInt(hours, 10) + 12;
  }
  return `${hours.toString().padStart(2, '0')}:${minutes}`;
}

function convertTo12Hour(time24) {
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${period}`;
}

// Q&A Questions Component
function toMultiline(text) {
  if (!text) return '';
  return text
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\n');
}

function SortableQuestionCard({ question, onToggleSolved, formatDate }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  const isSolved = question.solved || false;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`qa-mobile-card-${question.id}`}
      className={`rounded-xl border ${
        isSolved ? 'border-green-700/60 bg-green-950/20' : 'border-gray-700 bg-gray-800'
      } ${isDragging ? 'shadow-2xl ring-2 ring-red-500/50' : 'shadow-sm'} overflow-hidden`}
    >
      <div className="flex items-stretch">
        {/* Drag handle */}
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
          data-testid={`qa-mobile-drag-${question.id}`}
          className="flex-shrink-0 flex items-center justify-center px-3 bg-gray-900/60 text-gray-500 hover:text-gray-300 touch-none cursor-grab active:cursor-grabbing"
          style={{ touchAction: 'none' }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" />
            <circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" />
            <circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
          </svg>
        </button>

        {/* Body */}
        <div className="flex-1 min-w-0 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className={`text-base font-semibold truncate ${isSolved ? 'text-green-300 line-through' : 'text-white'}`}>
                {question.name || 'Unnamed'}
              </p>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                {question.studio && question.studio !== '-' && (
                  <span className="text-xs text-gray-400 truncate">{question.studio}</span>
                )}
                {question.appointment_date && question.appointment_date !== 'null' && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">
                    {question.appointment_date}
                  </span>
                )}
              </div>
            </div>

            {/* Solved toggle */}
            <label
              className="flex-shrink-0 flex flex-col items-center gap-1 cursor-pointer select-none"
              onPointerDown={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={isSolved}
                onChange={() => onToggleSolved(question.id)}
                data-testid={`qa-mobile-solved-${question.id}`}
                className="h-6 w-6 text-green-600 focus:ring-green-500 border-gray-600 rounded bg-gray-700"
              />
              <span className={`text-[10px] uppercase tracking-wide ${isSolved ? 'text-green-400' : 'text-gray-500'}`}>
                Solved
              </span>
            </label>
          </div>

          {question.issue && (
            <p className="text-sm text-gray-300 mt-2 leading-relaxed whitespace-pre-wrap break-words">
              {toMultiline(question.issue)}
            </p>
          )}

          {question.screen_video && question.screen_video !== '-' && (
            <a
              href={question.screen_video}
              target="_blank"
              rel="noopener noreferrer"
              onPointerDown={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 mt-3 text-sm text-blue-400 hover:text-blue-300 underline"
              data-testid={`qa-mobile-recording-${question.id}`}
            >
              🎥 View Recording
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function QAQuestions({ token, onLogout, onViewChange }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('today'); // today, all, future
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [message, setMessage] = useState('');
  
  // Add New Question Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newQuestion, setNewQuestion] = useState({
    name: '',
    issue: '',
    appointment_date: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Call Time Settings state
  const [callTimeSettings, setCallTimeSettings] = useState({
    call_time: '12:00 PM',
    mp3_url: 'https://storage.googleapis.com/msgsndr/XoOjZA2ND45EYBsBLntN/media/691dcf7a59e47b4aac438ec0.mp3'
  });
  const [savingCallTime, setSavingCallTime] = useState(false);
  const [controlsOpen, setControlsOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } })
  );

  const handleDndDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = questions.findIndex(q => q.id === active.id);
    const newIndex = questions.findIndex(q => q.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(questions, oldIndex, newIndex);
    persistReorder(reordered);
  };

  useEffect(() => {
    loadQuestions();
    loadCallTimeSettings();
  }, [dateFilter]);

  // Real-time sync - poll every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadQuestions();
    }, 3000);

    return () => clearInterval(interval);
  }, [dateFilter]);

  const loadQuestions = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/qa-questions?date_filter=${dateFilter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setQuestions(data.questions);
      }
    } catch (error) {
      console.error('Failed to load Q&A questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCallTimeSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/call-time-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCallTimeSettings(data);
      }
    } catch (error) {
      console.error('Failed to load call time settings:', error);
    }
  };

  const saveCallTimeSettings = async () => {
    setSavingCallTime(true);
    try {
      const response = await fetch(`${API_BASE}/api/call-time-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(callTimeSettings)
      });
      if (response.ok) {
        showMessage('Call time settings saved successfully!', 'success');
      } else {
        showMessage('Failed to save call time settings', 'error');
      }
    } catch (error) {
      console.error('Error saving call time settings:', error);
      showMessage('Failed to save call time settings', 'error');
    } finally {
      setSavingCallTime(false);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    // Create new order
    const newQuestions = [...questions];
    const draggedItem = newQuestions[draggedIndex];
    newQuestions.splice(draggedIndex, 1);
    newQuestions.splice(dropIndex, 0, draggedItem);

    setQuestions(newQuestions);
    setDraggedIndex(null);

    // Update order on server
    try {
      const questionIds = newQuestions.map(q => q.id);
      await fetch(`${API_BASE}/api/qa-questions/reorder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(questionIds)
      });
      showMessage('Questions reordered successfully!', 'success');
    } catch (error) {
      console.error('Error reordering questions:', error);
      showMessage('Failed to reorder questions', 'error');
      // Reload to get correct order
      loadQuestions();
    }
  };

  const persistReorder = async (newQuestions) => {
    setQuestions(newQuestions);
    try {
      const questionIds = newQuestions.map(q => q.id);
      await fetch(`${API_BASE}/api/qa-questions/reorder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(questionIds)
      });
      showMessage('Questions reordered successfully!', 'success');
    } catch (error) {
      console.error('Error reordering questions:', error);
      showMessage('Failed to reorder questions', 'error');
      loadQuestions();
    }
  };

  const handleCellEdit = (questionId, field, currentValue) => {
    setEditingCell(`${questionId}-${field}`);
    setEditValue(currentValue || '');
  };

  const handleCellSave = async (questionId, field) => {
    try {
      const updateData = {};
      
      if (field === 'solved') {
        updateData[field] = !questions.find(q => q.id === questionId)[field];
      } else {
        updateData[field] = editValue;
      }

      const response = await fetch(`${API_BASE}/api/qa-questions/${questionId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        loadQuestions();
        showMessage('Question updated successfully!', 'success');
      } else {
        showMessage('Failed to update question', 'error');
      }
    } catch (error) {
      console.error('Error updating question:', error);
      showMessage('Failed to update question', 'error');
    }
    
    setEditingCell(null);
    setEditValue('');
  };

  const handleCellCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(''), 3000);
  };

  // Get today's date in MM/DD/YY format for default
  const getTodayFormatted = () => {
    const today = new Date();
    return `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear().toString().substr(-2)}`;
  };

  // Handle Add New Question
  const handleAddQuestion = () => {
    setNewQuestion({
      name: '',
      issue: '',
      appointment_date: getTodayFormatted()
    });
    setShowAddModal(true);
  };

  const handleAddModalClose = () => {
    setShowAddModal(false);
    setNewQuestion({ name: '', issue: '', appointment_date: '' });
    setSubmitting(false);
  };

  const handleAddModalSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Create the payload for the fast admin endpoint
      const payload = {
        name: newQuestion.name,
        studio: 'Admin',
        topic: 'Manual Entry',
        issue: newQuestion.issue,
        submitted: new Date().toISOString(),
        screen_video: null,
        appointment_date: newQuestion.appointment_date
      };

      const response = await fetch(`${API_BASE}/api/admin/qa-questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        showMessage('Question added successfully!', 'success');
        handleAddModalClose();
        loadQuestions(); // Refresh the list
      } else {
        showMessage('Failed to add question', 'error');
      }
    } catch (error) {
      console.error('Error adding question:', error);
      showMessage('Failed to add question', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <p className="mt-4 text-gray-300">Loading Q&A questions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Toast Message */}
      {message && (
        <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
          message.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
        }`}>
          {message.text}
        </div>
      )}

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="flex justify-between items-center gap-4 mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Q&A Questions</h1>
            <p className="text-gray-400 mt-1 text-sm md:text-base">Manage questions submitted for live calls</p>
          </div>
          <button
            onClick={() => setControlsOpen(!controlsOpen)}
            aria-expanded={controlsOpen}
            data-testid="qa-controls-toggle"
            className="flex-shrink-0 flex items-center gap-2 bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-600 text-sm md:text-base"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            <span>Menu</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={`transition-transform duration-200 ${controlsOpen ? 'rotate-180' : ''}`}><polyline points="6 9 12 15 18 9"/></svg>
          </button>
        </div>

        {/* Collapsible controls (collapsed by default) */}
        {controlsOpen && (
          <div className="mb-6 bg-gray-800/60 border border-gray-700 rounded-lg p-4 space-y-4" data-testid="qa-controls-panel">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => onViewChange('admin-dashboard')}
                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 text-sm md:text-base"
              >
                Back to Admin
              </button>
              <button
                onClick={() => window.open('/call-queue', '_blank')}
                className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 text-sm md:text-base"
              >
                View Call Queue
              </button>
              <button
                onClick={onLogout}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm md:text-base"
              >
                Logout
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Call Date Scheduled Filter:
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleAddQuestion}
                  className="bg-green-600 text-white px-4 py-2 rounded font-medium hover:bg-green-700 text-sm md:text-base"
                  data-testid="qa-add-question-btn"
                >
                  Add a new question
                </button>
                <button
                  onClick={() => setDateFilter('today')}
                  className={`px-4 py-2 rounded font-medium text-sm md:text-base ${
                    dateFilter === 'today' 
                      ? 'bg-red-600 text-white' 
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Today (Default)
                </button>
                <button
                  onClick={() => setDateFilter('all')}
                  className={`px-4 py-2 rounded font-medium text-sm md:text-base ${
                    dateFilter === 'all' 
                      ? 'bg-red-600 text-white' 
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setDateFilter('future')}
                  className={`px-4 py-2 rounded font-medium text-sm md:text-base ${
                    dateFilter === 'future' 
                      ? 'bg-red-600 text-white' 
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Future (Including Today)
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Call Time Settings */}
        <div className="mb-6 hidden md:block bg-gray-800 p-4 rounded-lg border border-gray-700">
          <label className="block text-sm font-medium text-gray-300 mb-3">
            Call Time Settings:
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Call Time */}
            <div>
              <label className="block text-xs text-gray-400 mb-1">Call Time (EST)</label>
              <input
                type="time"
                value={callTimeSettings.call_time.includes('AM') || callTimeSettings.call_time.includes('PM') 
                  ? convertTo24Hour(callTimeSettings.call_time) 
                  : callTimeSettings.call_time}
                onChange={(e) => {
                  const time24 = e.target.value;
                  const time12 = convertTo12Hour(time24);
                  setCallTimeSettings({...callTimeSettings, call_time: time12});
                }}
                className="w-full px-3 py-2 border border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white"
              />
            </div>
            
            {/* MP3 URL */}
            <div>
              <label className="block text-xs text-gray-400 mb-1">Countdown Music URL</label>
              <input
                type="url"
                value={callTimeSettings.mp3_url}
                onChange={(e) => setCallTimeSettings({...callTimeSettings, mp3_url: e.target.value})}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white text-sm"
              />
            </div>
            
            {/* Save Button */}
            <div className="flex items-end">
              <button
                onClick={saveCallTimeSettings}
                disabled={savingCallTime}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                {savingCallTime ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>

        {/* Questions Table */}
        {questions.length > 0 ? (
          <>
          {/* Desktop table (md and up) */}
          <div className="hidden md:block bg-gray-800 rounded-lg overflow-hidden border border-gray-700">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-16">
                      Solved
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-48">
                      Customer Info
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider flex-1 min-w-96">
                      Details & Media
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-48">
                      Notes
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-32">
                      Call Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {questions.map((question, index) => (
                    <tr
                      key={question.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index)}
                      className="hover:bg-gray-750 cursor-move"
                    >
                      {/* Solved Checkbox */}
                      <td className="px-4 py-4 whitespace-nowrap align-top">
                        <input
                          type="checkbox"
                          checked={question.solved || false}
                          onChange={() => handleCellSave(question.id, 'solved')}
                          className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-600 rounded bg-gray-700"
                        />
                      </td>

                      {/* Customer Info Column (Name, Studio, Topic, Date Submitted stacked) */}
                      <td className="px-4 py-4 align-top">
                        <div className="space-y-2">
                          {/* Name - Editable */}
                          <div>
                            <span className="text-xs text-gray-400 block">Name:</span>
                            {editingCell === `${question.id}-name` ? (
                              <div className="flex space-x-2">
                                <input
                                  type="text"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="bg-gray-700 text-white px-2 py-1 rounded text-sm w-full"
                                  onKeyPress={(e) => e.key === 'Enter' && handleCellSave(question.id, 'name')}
                                />
                                <button
                                  onClick={() => handleCellSave(question.id, 'name')}
                                  className="text-green-400 hover:text-green-300 text-xs"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={handleCellCancel}
                                  className="text-red-400 hover:text-red-300 text-xs"
                                >
                                  ✗
                                </button>
                              </div>
                            ) : (
                              <div
                                onClick={() => handleCellEdit(question.id, 'name', question.name)}
                                className="text-white cursor-pointer hover:bg-gray-700 px-2 py-1 rounded text-sm font-medium"
                              >
                                {question.name || 'Add name...'}
                              </div>
                            )}
                          </div>

                          {/* Studio - Editable */}
                          <div>
                            <span className="text-xs text-gray-400 block">Studio:</span>
                            {editingCell === `${question.id}-studio` ? (
                              <div className="flex space-x-2">
                                <input
                                  type="text"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="bg-gray-700 text-white px-2 py-1 rounded text-sm w-full"
                                  onKeyPress={(e) => e.key === 'Enter' && handleCellSave(question.id, 'studio')}
                                />
                                <button
                                  onClick={() => handleCellSave(question.id, 'studio')}
                                  className="text-green-400 hover:text-green-300 text-xs"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={handleCellCancel}
                                  className="text-red-400 hover:text-red-300 text-xs"
                                >
                                  ✗
                                </button>
                              </div>
                            ) : (
                              <div
                                onClick={() => handleCellEdit(question.id, 'studio', question.studio)}
                                className="text-gray-300 cursor-pointer hover:bg-gray-700 px-2 py-1 rounded text-sm"
                              >
                                {question.studio || 'Add studio...'}
                              </div>
                            )}
                          </div>

                          {/* Topic - Editable */}
                          <div>
                            <span className="text-xs text-gray-400 block">Topic:</span>
                            {editingCell === `${question.id}-topic` ? (
                              <div className="flex space-x-2">
                                <input
                                  type="text"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="bg-gray-700 text-white px-2 py-1 rounded text-sm w-full"
                                  onKeyPress={(e) => e.key === 'Enter' && handleCellSave(question.id, 'topic')}
                                />
                                <button
                                  onClick={() => handleCellSave(question.id, 'topic')}
                                  className="text-green-400 hover:text-green-300 text-xs"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={handleCellCancel}
                                  className="text-red-400 hover:text-red-300 text-xs"
                                >
                                  ✗
                                </button>
                              </div>
                            ) : (
                              <div
                                onClick={() => handleCellEdit(question.id, 'topic', question.topic)}
                                className="text-gray-300 cursor-pointer hover:bg-gray-700 px-2 py-1 rounded text-sm"
                              >
                                {question.topic || 'Add topic...'}
                              </div>
                            )}
                          </div>

                          {/* Date Submitted - Editable */}
                          <div>
                            <span className="text-xs text-gray-400 block">Submitted:</span>
                            {editingCell === `${question.id}-submitted` ? (
                              <div className="flex space-x-2">
                                <input
                                  type="text"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="bg-gray-700 text-white px-2 py-1 rounded text-sm w-full"
                                  onKeyPress={(e) => e.key === 'Enter' && handleCellSave(question.id, 'submitted')}
                                />
                                <button
                                  onClick={() => handleCellSave(question.id, 'submitted')}
                                  className="text-green-400 hover:text-green-300 text-xs"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={handleCellCancel}
                                  className="text-red-400 hover:text-red-300 text-xs"
                                >
                                  ✗
                                </button>
                              </div>
                            ) : (
                              <div
                                onClick={() => handleCellEdit(question.id, 'submitted', question.submitted)}
                                className="text-gray-300 cursor-pointer hover:bg-gray-700 px-2 py-1 rounded text-sm"
                              >
                                {formatDate(question.submitted)}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Details & Media Column */}
                      <td className="px-4 py-4 align-top">
                        <div className="space-y-3">
                          {/* Details (Issue) - Editable with wider paragraph style */}
                          <div>
                            <span className="text-xs text-gray-400 block mb-1">Details:</span>
                            {editingCell === `${question.id}-issue` ? (
                              <div className="space-y-2">
                                <textarea
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="bg-gray-700 text-white px-3 py-2 rounded text-sm w-full resize-none"
                                  rows="4"
                                  placeholder="Enter question details..."
                                />
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleCellSave(question.id, 'issue')}
                                    className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCellCancel}
                                    className="bg-gray-600 text-white px-3 py-1 rounded text-xs hover:bg-gray-700"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div
                                onClick={() => handleCellEdit(question.id, 'issue', question.issue)}
                                className="text-gray-300 cursor-pointer hover:bg-gray-700 px-3 py-2 rounded text-sm leading-relaxed whitespace-pre-wrap"
                                style={{ minHeight: '60px', wordWrap: 'break-word' }}
                              >
                                {question.issue ? toMultiline(question.issue) : 'Click to add question details...'}
                              </div>
                            )}
                          </div>

                          {/* Screen Video - Under Details */}
                          <div>
                            <span className="text-xs text-gray-400 block mb-1">Screen Recording:</span>
                            {editingCell === `${question.id}-screen_video` ? (
                              <div className="space-y-2">
                                <input
                                  type="text"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="bg-gray-700 text-white px-3 py-2 rounded text-sm w-full"
                                  placeholder="Enter video URL..."
                                  onKeyPress={(e) => e.key === 'Enter' && handleCellSave(question.id, 'screen_video')}
                                />
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleCellSave(question.id, 'screen_video')}
                                    className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCellCancel}
                                    className="bg-gray-600 text-white px-3 py-1 rounded text-xs hover:bg-gray-700"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div
                                onClick={() => handleCellEdit(question.id, 'screen_video', question.screen_video)}
                                className="cursor-pointer hover:bg-gray-700 px-3 py-2 rounded text-sm"
                              >
                                {question.screen_video && question.screen_video !== '-' ? (
                                  <a 
                                    href={question.screen_video} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-400 hover:text-blue-300 underline inline-flex items-center gap-1"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    🎥 View Recording
                                  </a>
                                ) : (
                                  <span className="text-gray-500 italic">No recording</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Notes - Editable */}
                      <td className="px-4 py-4 align-top">
                        {editingCell === `${question.id}-notes` ? (
                          <div className="space-y-2">
                            <textarea
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="bg-gray-700 text-white px-3 py-2 rounded text-sm w-full resize-none"
                              rows="3"
                              placeholder="Add notes..."
                            />
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleCellSave(question.id, 'notes')}
                                className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCellCancel}
                                className="bg-gray-600 text-white px-3 py-1 rounded text-xs hover:bg-gray-700"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            onClick={() => handleCellEdit(question.id, 'notes', question.notes)}
                            className="text-gray-300 cursor-pointer hover:bg-gray-700 px-3 py-2 rounded text-sm leading-relaxed"
                            style={{ minHeight: '40px' }}
                          >
                            {question.notes || 'Add notes...'}
                          </div>
                        )}
                      </td>

                      {/* Call Date Scheduled - Editable */}
                      <td className="px-4 py-4 whitespace-nowrap align-top">
                        {editingCell === `${question.id}-appointment_date` ? (
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="bg-gray-700 text-white px-2 py-1 rounded text-sm w-full"
                              placeholder="MM/DD/YYYY"
                              onKeyPress={(e) => e.key === 'Enter' && handleCellSave(question.id, 'appointment_date')}
                            />
                            <div className="flex space-x-1">
                              <button
                                onClick={() => handleCellSave(question.id, 'appointment_date')}
                                className="text-green-400 hover:text-green-300 text-xs"
                              >
                                ✓
                              </button>
                              <button
                                onClick={handleCellCancel}
                                className="text-red-400 hover:text-red-300 text-xs"
                              >
                                ✗
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            onClick={() => handleCellEdit(question.id, 'appointment_date', question.appointment_date)}
                            className="text-gray-300 cursor-pointer hover:bg-gray-700 px-2 py-1 rounded text-sm"
                          >
                            {question.appointment_date && question.appointment_date !== 'null' 
                              ? question.appointment_date 
                              : 'No date'}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile card list (below md) */}
          <div className="md:hidden">
            <p className="text-xs text-gray-500 mb-3">
              Press &amp; hold the <span className="text-gray-400">⠿</span> handle to drag &amp; reorder. Tap the box to mark solved.
            </p>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDndDragEnd}
            >
              <SortableContext
                items={questions.map(q => q.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3" data-testid="qa-mobile-list">
                  {questions.map((question) => (
                    <SortableQuestionCard
                      key={question.id}
                      question={question}
                      onToggleSolved={(id) => handleCellSave(id, 'solved')}
                      formatDate={formatDate}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
          </>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-xl font-semibold text-white mb-2">No Q&A Questions</h3>
            <p className="text-gray-400">
              {dateFilter === 'today' 
                ? 'No questions submitted for today\'s call yet.'
                : dateFilter === 'future'
                ? 'No questions scheduled for future calls.'
                : 'No Q&A questions have been submitted yet.'
              }
            </p>
          </div>
        )}
      </div>

      {/* Add New Question Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg shadow-lg max-w-md w-full border border-gray-700">
            <div className="flex justify-between items-center p-6 border-b border-gray-700">
              <h3 className="text-xl font-semibold text-white">Add New Question</h3>
              <button
                onClick={handleAddModalClose}
                className="text-gray-400 hover:text-gray-200 text-2xl"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleAddModalSubmit} className="p-6 space-y-4">
              {/* Name Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={newQuestion.name}
                  onChange={(e) => setNewQuestion({...newQuestion, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white placeholder-gray-400"
                  placeholder="Enter customer name"
                  required
                />
              </div>

              {/* Details Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Details
                </label>
                <textarea
                  value={newQuestion.issue}
                  onChange={(e) => setNewQuestion({...newQuestion, issue: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white placeholder-gray-400"
                  placeholder="Enter question details"
                  rows={4}
                  required
                />
              </div>

              {/* Date Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Date (MM/DD/YY)
                </label>
                <input
                  type="text"
                  value={newQuestion.appointment_date}
                  onChange={(e) => setNewQuestion({...newQuestion, appointment_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-gray-700 text-white placeholder-gray-400"
                  placeholder="MM/DD/YY"
                  required
                />
              </div>

              {/* Submit Buttons */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleAddModalClose}
                  className="flex-1 bg-gray-600 text-white py-2 px-4 rounded hover:bg-gray-700"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {submitting ? 'Adding...' : 'Add Question'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Call Queue Component (Full Screen)
function CallQueue() {
  const [queueQuestions, setQueueQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState('');

  useEffect(() => {
    // Check authentication when component loads
    const token = localStorage.getItem('tfun_admin_token');
    if (!token) {
      // Redirect to home page if not authenticated
      window.location.href = '/';
      return;
    }

    loadCallQueue();
    // Set current date
    const today = new Date();
    setCurrentDate(today.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }));
  }, []);

  // Real-time sync - poll every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadCallQueue();
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const loadCallQueue = async () => {
    try {
      const token = localStorage.getItem('tfun_admin_token');
      if (!token) {
        window.location.href = '/';
        return;
      }
      
      const response = await fetch(`${API_BASE}/api/qa-questions/queue`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setQueueQuestions(data.questions || []);
      } else if (response.status === 401) {
        // Token expired or invalid
        localStorage.removeItem('tfun_admin_token');
        window.location.href = '/';
        return;
      }
    } catch (error) {
      console.error('Failed to load call queue:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-4 border-white"></div>
          <p className="mt-4 text-white text-xl">Loading call queue...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="flex h-screen">
        {/* Left Column - Queue List (20%) */}
        <div className="w-1/5 p-6 border-r border-gray-700 overflow-y-auto">
          <h2 className="text-2xl font-bold text-white mb-6">In the Queue</h2>
          
          {queueQuestions.length > 0 ? (
            <div className="space-y-4">
              {queueQuestions.map((question, index) => (
                <div key={question.id} className="pb-4">
                  <div className="font-bold text-white text-lg mb-2">
                    {question.name}
                  </div>
                  <div className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap break-words">
                    {toMultiline(question.issue)}
                  </div>
                  {index < queueQuestions.length - 1 && (
                    <hr className="mt-4 border-gray-600" />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-6xl mb-4">✅</div>
              <p className="text-gray-300 text-lg">That's all the questions for today!</p>
            </div>
          )}
        </div>

        {/* Right Column - Welcome Message (80%) */}
        <div className="w-4/5 p-12 flex flex-col justify-center items-center">
          <div className="max-w-4xl text-center space-y-8">
            <h1 className="text-6xl font-bold text-white mb-8">
              Welcome to the Group Q&A Support Call!
            </h1>
            
            <div className="text-2xl text-gray-200 mb-12">
              Today is <span className="font-semibold text-white">{currentDate}</span>
            </div>
            
            <div className="text-xl text-gray-200 space-y-6 leading-relaxed max-w-3xl mx-auto">
              <div className="bg-gray-800/50 p-8 rounded-lg">
                <h2 className="text-2xl font-semibold text-white mb-6">REMEMBER: This is a group troubleshooting call.</h2>
                
                <div className="space-y-4 text-left">
                  <div className="flex items-start space-x-4">
                    <span className="text-red-400 text-2xl">•</span>
                    <p>Please be respectful and courteous.</p>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <span className="text-red-400 text-2xl">•</span>
                    <p>If we unmute you, please make sure you're in a quiet place away from background noise.</p>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <span className="text-red-400 text-2xl">•</span>
                    <p>Please do not tell long-winded stories or rabbit trail onto side topics.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Whitelabel Articles Tab Component
function WhitelabelTab({ token, categories, onViewChange, onReload }) {
  const [url, setUrl] = useState('');
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [recentArticles, setRecentArticles] = useState([]);
  
  useEffect(() => {
    loadRecentWhitelabelArticles();
  }, []);

  const loadRecentWhitelabelArticles = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/articles?whitelabel=true&limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRecentArticles(data.filter(article => article.whitelabel_source));
      }
    } catch (error) {
      console.error('Failed to load whitelabel articles:', error);
    }
  };

  const handleProcessUrl = async () => {
    if (!url.trim()) {
      showMessage('Please enter a valid URL', 'error');
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(`${API_BASE}/api/whitelabel/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url: url.trim() })
      });

      if (response.ok) {
        const result = await response.json();
        showMessage(`Article created successfully: "${result.title}"`, 'success');
        setUrl('');
        loadRecentWhitelabelArticles();
        onReload(); // Reload articles in main dashboard
      } else {
        const errorData = await response.json();
        showMessage(errorData.detail || 'Failed to process article', 'error');
      }
    } catch (error) {
      console.error('Error processing URL:', error);
      showMessage('Error processing article', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(''), 5000);
  };

  return (
    <div className="space-y-6">
      {/* Toast Message */}
      {message && (
        <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
          message.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
        }`}>
          {message.text}
        </div>
      )}

      {/* Header */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h2 className="text-2xl font-bold text-white mb-2">🏷️ Whitelabel Articles</h2>
        <p className="text-gray-300 mb-4">
          Import and whitelabel support articles from GoHighLevel. The AI will automatically replace brand names with "The Follow Up Ninja" 
          and categorize the content appropriately.
        </p>
      </div>

      {/* URL Input Form */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4">Import Article from URL</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Article URL (from help.gohighlevel.com)
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://help.gohighlevel.com/support/solutions/articles/..."
              className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-red-500 focus:outline-none"
              disabled={processing}
            />
          </div>
          
          <button
            onClick={handleProcessUrl}
            disabled={processing || !url.trim()}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              processing || !url.trim()
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700'
            }`}
          >
            {processing ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Processing Article...
              </div>
            ) : (
              'Import & Process Article'
            )}
          </button>
        </div>

        {processing && (
          <div className="mt-4 bg-blue-900/20 border border-blue-700 p-4 rounded-lg">
            <h4 className="text-blue-300 font-medium mb-2">Processing Steps:</h4>
            <ol className="text-sm text-blue-200 space-y-1">
              <li>✓ Fetching article content from URL</li>
              <li>✓ Extracting article text and images</li>
              <li>✓ Replacing brand names with "The Follow Up Ninja"</li>
              <li>✓ Removing external links</li>
              <li>✓ AI determining appropriate category</li>
              <li>✓ Creating unpublished article for review</li>
            </ol>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-blue-900/20 border border-blue-700 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-blue-300 mb-3">📋 Instructions</h3>
        <ul className="text-blue-200 space-y-2 text-sm">
          <li><span className="text-blue-400">•</span> Enter a GoHighLevel support article URL</li>
          <li><span className="text-blue-400">•</span> The AI will automatically replace "HighLevel" and "GoHighLevel" with "The Follow Up Ninja"</li>
          <li><span className="text-blue-400">•</span> External links and their context will be removed</li>
          <li><span className="text-blue-400">•</span> Images from the original article will be preserved</li>
          <li><span className="text-blue-400">•</span> The article will be created as unpublished for your review</li>
          <li><span className="text-blue-400">•</span> AI will automatically select an appropriate category based on content</li>
        </ul>
      </div>

      {/* Recent Whitelabel Articles */}
      {recentArticles.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">Recently Imported Articles</h3>
          <div className="space-y-3">
            {recentArticles.map(article => (
              <div key={article.id} className="flex justify-between items-center bg-gray-700 p-4 rounded-lg">
                <div>
                  <h4 className="text-white font-medium">{article.title}</h4>
                  <p className="text-gray-400 text-sm">
                    {article.published ? 'Published' : 'Draft'} • 
                    Created {new Date(article.created_at).toLocaleDateString()}
                  </p>
                  {article.whitelabel_source && (
                    <p className="text-blue-400 text-xs mt-1">
                      Source: {new URL(article.whitelabel_source).pathname}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => onViewChange('admin-edit', article.id)}
                  className="bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700 text-sm"
                >
                  Edit Article
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Date formatting helper function
function formatTodayDate() {
  const today = new Date();
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const month = months[today.getMonth()];
  const day = today.getDate();
  const year = today.getFullYear();
  
  // Add ordinal suffix to day
  const getOrdinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };
  
  return `${month} ${getOrdinal(day)}, ${year}`;
}

// Countdown Page Component
function CountdownPage({ token, onLogout, onViewChange }) {
  const [callTimeSettings, setCallTimeSettings] = useState({
    call_time: '12:00 PM',
    mp3_url: 'https://storage.googleapis.com/msgsndr/XoOjZA2ND45EYBsBLntN/media/691dcf7a59e47b4aac438ec0.mp3'
  });
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [audioRef, setAudioRef] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [hasRedirected, setHasRedirected] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [audioError, setAudioError] = useState(false);

  // Load call time settings
  useEffect(() => {
    loadCallTimeSettings();
  }, []);

  // Initialize audio only when enabled
  useEffect(() => {
    if (callTimeSettings.mp3_url && audioEnabled && !audioRef) {
      const audio = new Audio(callTimeSettings.mp3_url);
      audio.addEventListener('loadedmetadata', () => {
        setAudioDuration(audio.duration);
        console.log(`Audio duration: ${audio.duration} seconds`);
      });
      audio.addEventListener('error', (e) => {
        console.error('Audio load error:', e);
        setAudioError(true);
      });
      setAudioRef(audio);
    }
  }, [callTimeSettings.mp3_url, audioEnabled, audioRef]);

  // Countdown timer
  useEffect(() => {
    if (!callTimeSettings.call_time || isLoading) return;

    const interval = setInterval(() => {
      const now = new Date();
      const targetTime = getTargetTime(callTimeSettings.call_time);
      const timeDiff = targetTime.getTime() - now.getTime();

      // Debug logging for countdown state
      if (timeDiff <= 5000) { // Log when less than 5 seconds remaining
        console.log(`⏱️ Debug: timeDiff=${timeDiff}ms, hasRedirected=${hasRedirected}, target=${targetTime.toLocaleTimeString()}`);
      }

      if (timeDiff <= 0 && !hasRedirected) {
        // Time's up! Redirect
        console.log('🚀 COUNTDOWN FINISHED - Redirecting to call queue!');
        clearInterval(interval); // Clear interval immediately
        setHasRedirected(true);
        
        // Stop audio if playing
        if (audioRef && !audioRef.paused) {
          audioRef.pause();
          console.log('🔊 Audio stopped for redirect');
        }
        
        // Show final countdown state
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
        
        // Redirect after a brief delay to ensure state updates
        setTimeout(() => {
          console.log('🌐 Executing redirect to call queue...');
          
          try {
            // Try multiple redirect methods for maximum compatibility
            if (window.location.replace) {
              console.log('Using window.location.replace...');
              window.location.replace('https://support.thefollowupninja.com/call-queue');
            } else if (window.location.assign) {
              console.log('Using window.location.assign...');
              window.location.assign('https://support.thefollowupninja.com/call-queue');
            } else {
              console.log('Using window.location.href...');
              window.location.href = 'https://support.thefollowupninja.com/call-queue';
            }
            
            // Backup redirect attempt after 1 second if first attempt fails
            setTimeout(() => {
              console.log('🔄 Backup redirect attempt...');
              window.open('https://support.thefollowupninja.com/call-queue', '_self');
            }, 1000);
            
          } catch (error) {
            console.error('❌ Redirect failed:', error);
            // Final fallback - open in new tab
            console.log('🔄 Final fallback - opening in new tab...');
            window.open('https://support.thefollowupninja.com/call-queue', '_blank');
          }
        }, 500);
        
        return;
      }

      // Calculate time left
      const hours = Math.floor(timeDiff / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

      setTimeLeft({ hours, minutes, seconds });

      // Additional redirect trigger - when display shows 00:00:00 or 00:00
      if ((hours === 0 && minutes === 0 && seconds === 0) && !hasRedirected) {
        console.log('🚀 BACKUP TRIGGER - Countdown shows 00:00 - Redirecting!');
        clearInterval(interval);
        setHasRedirected(true);
        
        // Stop audio if playing
        if (audioRef && !audioRef.paused) {
          audioRef.pause();
          console.log('🔊 Audio stopped for backup redirect');
        }
        
        // Immediate redirect for backup trigger
        setTimeout(() => {
          console.log('🌐 Backup redirect executing...');
          try {
            window.location.href = 'https://support.thefollowupninja.com/call-queue';
          } catch (error) {
            console.error('❌ Backup redirect failed:', error);
            window.open('https://support.thefollowupninja.com/call-queue', '_blank');
          }
        }, 100);
      }

      // Handle audio timing (only if audio is enabled)
      if (audioEnabled && audioRef && audioDuration > 0 && timeDiff > 0) {
        const totalSecondsLeft = Math.floor(timeDiff / 1000);
        handleAudioTiming(totalSecondsLeft);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [callTimeSettings.call_time, isLoading, audioRef, audioDuration, hasRedirected, audioEnabled]);

  const loadCallTimeSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/call-time-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCallTimeSettings(data);
      }
    } catch (error) {
      console.error('Failed to load call time settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTargetTime = (timeString) => {
    const now = new Date();
    const [time, period] = timeString.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    
    // Convert to 24-hour format
    let targetHours = hours;
    if (period === 'PM' && hours !== 12) targetHours += 12;
    if (period === 'AM' && hours === 12) targetHours = 0;

    const target = new Date();
    target.setHours(targetHours, minutes, 0, 0);

    // If time has passed today, set for tomorrow
    if (target <= now) {
      target.setDate(target.getDate() + 1);
    }

    return target;
  };

  const handleAudioTiming = (secondsLeft) => {
    if (!audioRef || audioDuration === 0) return;

    // For testing/demo: start audio immediately when enabled (remove this later if not desired)
    // This ensures users can hear the audio right after enabling
    if (audioRef.paused && audioEnabled) {
      console.log(`Starting audio with ${secondsLeft} seconds left (${Math.floor(secondsLeft / 60)}:${(secondsLeft % 60).toString().padStart(2, '0')})`);
      audioRef.loop = true;
      audioRef.volume = 0.7; // Set reasonable volume
      audioRef.play().catch(e => console.log('Audio play failed:', e));
    }

    // Handle looping to end exactly at zero
    if (secondsLeft > 0 && audioRef.duration > 0 && !audioRef.paused) {
      // Calculate how far into the audio we should be to end at zero
      const cyclesUntilEnd = Math.ceil(secondsLeft / audioDuration);
      const targetTimeInCycle = secondsLeft % audioDuration;
      const shouldBeAtTime = audioDuration - targetTimeInCycle;
      
      // Sync audio position if it's significantly off (more than 2 seconds)
      const currentTime = audioRef.currentTime;
      const timeDiff = Math.abs(currentTime - shouldBeAtTime);
      
      if (timeDiff > 2) {
        console.log(`Syncing audio: current=${currentTime.toFixed(1)}s, should be=${shouldBeAtTime.toFixed(1)}s`);
        audioRef.currentTime = shouldBeAtTime;
      }
    }

    // Stop audio when countdown reaches zero
    if (secondsLeft <= 0 && !audioRef.paused) {
      audioRef.pause();
      audioRef.currentTime = audioDuration; // End at the end of the track
      console.log('Audio stopped - countdown finished');
    }
  };

  const handleEnableSound = async () => {
    try {
      setAudioError(false);
      console.log('Attempting to enable audio...');
      
      // Test audio playback capability
      const testAudio = new Audio(callTimeSettings.mp3_url);
      
      // Wait for audio to load
      await new Promise((resolve, reject) => {
        testAudio.addEventListener('loadeddata', resolve);
        testAudio.addEventListener('error', reject);
        testAudio.load();
      });
      
      // Try to play a brief moment to test browser permission
      testAudio.volume = 0.1; // Low volume for test
      await testAudio.play();
      
      // Stop test audio
      testAudio.pause();
      testAudio.currentTime = 0;
      
      // If successful, enable audio
      setAudioEnabled(true);
      console.log('Audio enabled successfully - music will start with countdown');
      
    } catch (error) {
      console.error('Failed to enable audio:', error);
      setAudioError(true);
      
      // Still enable audio state in case the error is just with the test
      // The actual countdown audio might still work
      setAudioEnabled(true);
      console.log('Audio enabled with potential issues - will attempt playback during countdown');
    }
  };

  const handleStartCall = () => {
    window.location.href = 'https://support.thefollowupninja.com/call-queue';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <p className="mt-4 text-gray-300">Loading countdown...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black">
      {/* Main Content - Exactly one viewport height */}
      <div className="h-screen flex flex-col items-center justify-center text-center">
        {/* Today's Date */}
        <p className="text-3xl text-white italic mb-6">
          Today is {formatTodayDate()}
        </p>

        {/* Meeting Begins In - Combined on one line */}
        <h1 className="text-6xl font-bold mb-8" style={{ color: '#d4161d' }}>
          {timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0 ? 
            'MEETING STARTING NOW!' : 'MEETING BEGINS IN'
          }
        </h1>

        {/* Countdown Timer */}
        <div className={`text-9xl font-bold mb-12 font-mono tracking-wider ${
          timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0 
            ? 'text-red-500 animate-pulse' 
            : 'text-white'
        }`}>
          {timeLeft.hours > 0 ? (
            // Show HH:MM:SS when hours > 0
            <>
              {String(timeLeft.hours).padStart(2, '0')}:
              {String(timeLeft.minutes).padStart(2, '0')}:
              {String(timeLeft.seconds).padStart(2, '0')}
            </>
          ) : (
            // Show MM:SS when hours = 0
            <>
              {String(timeLeft.minutes).padStart(2, '0')}:
              {String(timeLeft.seconds).padStart(2, '0')}
            </>
          )}
        </div>

        {/* Logo */}
        <div className="mb-8">
          <img 
            src="https://storage.googleapis.com/msgsndr/XoOjZA2ND45EYBsBLntN/media/6604c93e4d7869b489d2d458.png"
            alt="The Follow Up Ninja"
            className="h-16 w-auto"
          />
        </div>

        {/* Speaker Check Message - Subtle flashing */}
        <p className="text-lg text-gray-300 animate-pulse flex items-center justify-center">
          Check your speakers and make sure you can hear the song playing
          <svg className="w-5 h-5 ml-2 text-gray-400" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
          </svg>
        </p>

        {/* Animated Sound Wave Bars - Show when audio is enabled and playing */}
        {audioEnabled && audioRef && !audioRef.paused && (
          <div className="w-full max-w-4xl mx-auto mt-4">
            <div className="flex items-end justify-center space-x-1 h-8">
              {[...Array(60)].map((_, i) => (
                <div
                  key={i}
                  className="bg-red-500 rounded-t-full opacity-70 flex-shrink-0"
                  style={{
                    width: '2px',
                    height: '8px',
                    animation: `soundWave${i % 12} ${0.8 + (i % 3) * 0.2}s ease-in-out infinite alternate`,
                    animationDelay: `${(i * 0.05)}s`
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Below-the-fold Section - Buttons only */}
      <div className="h-screen flex flex-col items-center justify-center space-y-8">
        {/* Buttons Section */}
        <div className="flex flex-col items-center space-y-6">
          {/* Enable Sound Button (show when audio is not enabled) */}
          {!audioEnabled && (
            <div className="text-center">
              <p className="text-gray-300 text-sm mb-4">
                Click below to enable countdown music
              </p>
              <button
                onClick={handleEnableSound}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors shadow-lg"
              >
                🔊 Enable Sound
              </button>
              {audioError && (
                <p className="text-red-400 text-sm mt-3">
                  Unable to enable sound. Audio may not be available in this browser.
                </p>
              )}
            </div>
          )}

          {/* Audio Status (show when enabled) */}
          {audioEnabled && (
            <div className="text-center">
              <p className="text-green-400 text-lg flex items-center justify-center mb-2">
                <span className="mr-2">🔊</span>
                Countdown music will play automatically
              </p>
              {/* Debug Info */}
              <div className="text-xs text-gray-400 text-center">
                Audio Duration: {audioDuration.toFixed(1)}s | 
                Playing: {audioRef && !audioRef.paused ? 'Yes' : 'No'} |
                Volume: {audioRef ? audioRef.volume.toFixed(1) : 'N/A'}
              </div>
            </div>
          )}

          {/* Start Call Button */}
          <button
            onClick={handleStartCall}
            className="bg-red-600 text-white px-8 py-4 rounded-lg text-xl font-medium hover:bg-red-700 transition-colors"
          >
            Start Call
          </button>
        </div>
      </div>
    </div>
  );
}

